/* eslint-disable @typescript-eslint/no-unused-vars, no-nested-ternary, consistent-return, react/no-array-index-key, react/require-default-props, @typescript-eslint/no-explicit-any, jsx-a11y/no-noninteractive-tabindex, react/no-unstable-nested-components, react/no-danger */
import React, { useContext, useMemo, useState, useEffect, useRef, useId } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion, useInView, animate } from "framer-motion"
import {
  HandRaisedIcon,
  ExclamationTriangleIcon,
  ChatBubbleBottomCenterTextIcon,
  ArrowTrendingUpIcon,
  HeartIcon,
  NoSymbolIcon,
  FaceFrownIcon,
  PhoneIcon,
  ClockIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { StaticImage } from "gatsby-plugin-image"
import SEO from "components/seo"
import Contact from "components/organisms/somebuddy/Contact"
import Hero from "components/organisms/annual-reports/Hero"
import SocialPlatformDrawer, {
  defaultPlatforms,
  getRiskDescriptionParagraphs,
  type PlatformId,
  type PlatformInfo,
} from "components/molecules/SocialPlatformDrawer"
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  LabelList,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts"
import QuoteCard from "components/molecules/annualReport/QuoteCard"
import MobileBottomSheet from "components/molecules/MobileBottomSheet"
import { NavBar } from "components/organisms/materials/NavBar"
import useTranslations from "hooks/useTranslations"
import { LocaleContext } from "contexts"
import YearBarChart from "components/molecules/charts/YearBarChart"
import { ageDistributionByYear as ageDistributionByYearData } from "data/ageDistribution"

const ANNUAL_REPORT_2025_STATIC = {
  aiCaseTileIds: ["sextortion", "kiusaaminen", "koulu"],
  teacherSectionText2SourceUrl:
    "https://www.sttinfo.fi/tiedote/71843478/oaj-selvitti-opettajista-tehdaan-aiempaa-herkemmin-rikosilmoituksia?publisherId=69817356",
  journeyVisualStartSourceUrl: "https://doi.org/10.2908/ISOC_CI_HM",
  // Shared with the UK schools page, which draws the same histogram — see
  // `src/data/ageDistribution.ts` for why it lives outside this file.
  ageDistributionByYear: ageDistributionByYearData,
  /** One row per year; CrimeTagsBox uses the latest. Keys = crime tag names, value = % of cases. */
  crimeTagsBoxData: [
    {
      year: "2025",
      "Lapsen seksuaalista itsemääräämisoikeutta loukkaavat rikokset": 51,
      Kunnianloukkaus: 40,
      "Laiton uhkaus": 16,
    },
  ],
  crimeTagColors: {
    Kunnianloukkaus: "#1a5f4a", // kkm-darkgreen
    "Lapsen seksuaalista itsemääräämisoikeutta loukkaavat rikokset": "#767676", // kkm-grey
    "Laiton uhkaus": "#7accb5", // kkm-lightgreen
  },
  /** Emoji or icon per crime tag for the grey box (replaces line chart). */
  crimeTagEmoji: {
    Kunnianloukkaus: "👿",
    "Lapsen seksuaalista itsemääräämisoikeutta loukkaavat rikokset": "🔞",
    "Laiton uhkaus": "🤬",
  },
  psychologicalSymptomsBarData: [
    { name: "Asia ahdistaa tai pelottaa minua", value: 53.8 },
    { name: "Mieli on maassa ja itkettää", value: 45.4 },
    { name: "Olen vihainen tai ärsyyntynyt", value: 39.5 },
    {
      name: "Minusta tuntuu että minua on nöyryytetty muiden silmissä",
      value: 32.1,
    },
    { name: "Koen itseni huonoksi tai epäonnistuneeksi", value: 24.7 },
    {
      name: "Minulla on vaikeuksia suorittaa arkisia asioita (esim. mennä kouluun)",
      value: 20.7,
    },
    { name: "Itsetuhoiset ajatukset tai käyttäytyminen", value: 19.9 },
    { name: "Minulla on vaikeuksia nukkua", value: 19.6 },
  ],
  /** Order: Rikos, Rikoksen uhka, Ei rikosta (legend order). Colors: rikos darkest, rikoksen uhka second, ei rikosta lightest. */
  seriousCasesPieData: [
    { name: "Rikos", value: 19.1, fill: "#1a5f4a" },
    { name: "Rikoksen uhka", value: 52.4, fill: "#3a9a7a" },
    { name: "Ei rikosta", value: 28.5, fill: "#7accb5" },
  ],
  perpetratorKnownPieData: [
    { name: "Koulusta", value: 59.2, fill: "#3a9a7a" },
    { name: "Muualta", value: 29.9, fill: "#1a5f4a" },
    { name: "Kaveripiireistä", value: 10.9, fill: "#7accb5" },
  ],
  teacherCasesPieData: [
    { name: "Oma tilanne", value: 30.7, fill: "#3a9a7a" },
    { name: "Oppilaan tilanne", value: 69.3, fill: "#1a5f4a" },
  ],
  totalCasesCount: 5000,
  /** Solved cases by year for line chart. Replace with real figures. */
  solvedCasesByYear: [
    { year: "2021", cases: 1800 },
    { year: "2022", cases: 2100 },
    { year: "2023", cases: 2350 },
    { year: "2024", cases: 2480 },
    { year: "2025", cases: 2500 },
  ],
  toldAdultPieData: [
    { name: "Kyllä", value: 57, fill: "#3a9a7a" },
    { name: "Ei", value: 37, fill: "#1a5f4a" },
    { name: "Ei tiedossa", value: 6, fill: "#7accb5" },
  ],
}

// File structure: data (ANNUAL_REPORT_2025_STATIC) → layout constants & components → chart components → page (AnnualReport2025Page) → export.
// To change report text width or flush-image offset, edit the constants in the "Report layout" block below.

// ─── Report layout (constants and helpers) ─────────────────────────────────────
// All report sections share one grid (1fr | text column | 1fr) so headings and body text align on every screen.
// Content width and flush-image offset use inline styles / injected CSS so changing the constants below actually
// updates the layout (Tailwind does not generate classes from template literals like max-w-[${n}rem]).

// Tuning (edit these and reload):
//   REPORT_CONTENT_WIDTH_REM  – text column width in rem (e.g. 50 = narrower, 70 = wider)
//   REPORT_FLUSH_OFFSET_REM   – how far flush images extend past section padding (e.g. 3 = 3rem negative margin)

const REPORT_CONTENT_WIDTH_REM = 60
const REPORT_SECTION_PADDING = "px-4 py-10 sm:px-6 lg:py-12 lg:px-8"
const REPORT_FLUSH_OFFSET_REM = 3
/** Alternating dark section backgrounds (first after hero = black; contact form uses zinc-800). */
const REPORT_BLACK_BG = "bg-zinc-900"
const REPORT_GRAY_BG = "bg-zinc-800"
/** Full-bleed bands: same shades as schools page (Divided green / yellow / light). Alternating normal/light per section type. */
const REPORT_TEAL_BG = "bg-kkm-green"
const REPORT_TEAL_LIGHT_BG = "bg-kkm-lightgreen"
const REPORT_WHITE_BG = "bg-white"
const REPORT_WHITE_LIGHT_BG = "bg-zinc-50"
const REPORT_YELLOW_BG = "bg-kkm-yellow"
const REPORT_YELLOW_LIGHT_BG = "bg-[hsl(38,99%,88%)]"

const REPORT_CONTENT_STYLE = {
  maxWidth: `${REPORT_CONTENT_WIDTH_REM}rem`,
  width: "100%",
}
/** Finnish convention: space before % (e.g. "10 %"). Use when displaying percentages to the user. */
const PCT_SUFFIX_FI = " %"
const REPORT_ROW_CLASS =
  "flex w-full flex-col items-center gap-10 lg:flex-row lg:items-center lg:gap-10"

function textWithHighlight(
  text: string,
  phrase: string,
  highlightClassName = "font-semibold text-zinc-200",
): React.ReactNode {
  if (!phrase || typeof text !== "string" || !text.includes(phrase)) return text
  const [before, ...rest] = text.split(phrase)
  const after = rest.join(phrase)
  return (
    <>
      {before}
      <span className={highlightClassName}>{phrase}</span>
      {after}
    </>
  )
}

const SOURCE_PLACEHOLDER = "[[SOURCE]]"

function textWithSourcePlaceholder(
  text: string,
  source?: { url?: string; label?: string },
  fallbackLabel?: string,
): React.ReactNode {
  if (typeof text !== "string") return text
  if (!source?.url) return text
  if (!text.includes(SOURCE_PLACEHOLDER)) {
    return (
      <>
        {text}{" "}
        <SourceLink
          url={source.url}
          label={source.label}
          fallbackLabel={fallbackLabel}
        />
      </>
    )
  }
  const [before, ...rest] = text.split(SOURCE_PLACEHOLDER)
  const after = rest.join(SOURCE_PLACEHOLDER)
  return (
    <>
      {before}
      <SourceLink
        url={source.url}
        label={source.label}
        fallbackLabel={fallbackLabel}
      />
      {after}
    </>
  )
}

const SWEDEN_HOTSPOTS = [
  {
    id: "support",
    left: "58%",
    top: "20%",
  },
  {
    id: "gang",
    left: "10%",
    top: "59%",
  },
  {
    id: "roblox",
    left: "50%",
    top: "85%",
  },
]

function NordicExpansionViz({
  activeId,
  onSelect,
  hotspots,
  imageAlt,
}: {
  activeId: string | null
  onSelect: (id: string | null) => void
  hotspots: Array<{
    id: string
    left: string
    top: string
    label: string
    text: string
  }>
  imageAlt: string
}) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const pillsRef = useRef<HTMLDivElement | null>(null)
  const pillsInView = useInView(pillsRef, { amount: 0.65, once: true })
  const setActiveId = onSelect
  const active = hotspots.find((h) => h.id === activeId) ?? null
  const hotspotsTopDown = [...hotspots].sort(
    (a, b) => parseFloat(a.top) - parseFloat(b.top),
  )

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveId?.(null)
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [])

  return (
    <div className="relative">
      <div className="relative overflow-visible">
        <StaticImage
          src="../../../images/annual-reports/sweden1.jpg"
          alt={imageAlt}
          layout="constrained"
          width={420}
          placeholder="blurred"
          className="mx-auto !h-auto !w-auto max-w-full"
          imgClassName="object-contain object-center"
        />

        {/* Hotspots (dot + label together) */}
        <motion.div
          ref={pillsRef}
          className="absolute inset-0"
          variants={swedenPillsContainerVariants}
          initial="hidden"
          animate={pillsInView ? "visible" : "hidden"}
        >
          {hotspotsTopDown.map((h, i) => {
          const isHovered = hoveredId === h.id
          const isActive = activeId === h.id
          const leftPct = parseFloat(h.left)
          const xShift = leftPct < 30 ? "0%" : leftPct > 70 ? "-100%" : "-50%"
          return (
            <div
              key={h.id}
              className="absolute"
              style={{
                left: h.left,
                top: h.top,
                transform: `translate(${xShift}, -50%)`,
              }}
            >
              <motion.div variants={swedenPillItemVariants}>
                <button
                  type="button"
                  onMouseEnter={() => setHoveredId(h.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => setActiveId?.(h.id)}
                  className={`group flex max-w-[70vw] items-center gap-2 rounded-xl bg-white px-3 py-2 text-left shadow-[0_14px_44px_rgba(0,0,0,0.24)] ring-2 backdrop-blur transition-[box-shadow,transform,border-color] hover:-translate-y-0.5 hover:shadow-[0_18px_54px_rgba(0,0,0,0.28)] focus:outline-none focus:ring-2 focus:ring-kkm-green/60 sm:max-w-none sm:rounded-full ${
                    isActive
                      ? "ring-kkm-green/50"
                      : isHovered
                        ? "ring-kkm-green/40"
                        : "ring-zinc-900/25"
                  }`}
                  aria-label={h.label}
                >
                  <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                    <motion.span
                      className="absolute inline-flex h-3.5 w-3.5 rounded-full bg-kkm-green/35"
                      animate={{
                        scale: isActive || isHovered ? [1, 1.9, 1] : [1, 1.5, 1],
                        opacity:
                          isActive || isHovered
                            ? [0.9, 0.2, 0.9]
                            : [0.7, 0.2, 0.7],
                      }}
                      transition={{
                        duration: 1.6 + (i % 3) * 0.1,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      aria-hidden
                    />
                    <span className="relative h-2.5 w-2.5 rounded-full bg-kkm-green shadow-[0_0_0_2px_rgba(255,255,255,0.95)]" />
                  </span>
                  <span
                    className={`text-xs font-bold tracking-tight transition sm:text-sm ${
                      isActive
                        ? "text-zinc-900"
                        : isHovered
                          ? "text-zinc-900"
                          : "text-zinc-700"
                    }`}
                  >
                    {h.label}
                  </span>
                </button>
              </motion.div>
            </div>
          )
        })}
        </motion.div>
      </div>
    </div>
  )
}

/** Duolingo-style path: narrow, small circular nodes, thick winding SVG path, icon + short label, tooltip for detail. */
const NODE_SIZE = 60
const PATH_STROKE = 6
const CENTER_X = 120
const PATH_WIDTH = 100
const ROWS = [0, 100, 200, 300, 400]
/** T-junction: trunk vertical; left/right use Q only at the junction, then strict V (vertical) and L (horizontal) only – no curves between stops. */
const RADIUS = 10
/** Start node is 60px tall (top 0–60); trunk starts at node bottom so the T is visible below the node. */
const NODE_BOTTOM_Y = 60
const JUNCTION_Y = 80
const JUNCTION_HORIZ_Y = JUNCTION_Y + RADIUS
const TRUNK_PATH = `M ${CENTER_X} ${NODE_BOTTOM_Y} L ${CENTER_X} ${JUNCTION_Y}`
const LEFT_LANE_X = 86
const RIGHT_LANE_X = 154
const PATH_END_Y = 430
/** Shadow path extends to bottom of viewBox; tail is visible then fades to transparent at the very end. */
const LEFT_PATH_FADE_END_Y = 424
const LEFT_PATH = `M ${CENTER_X} ${JUNCTION_Y} Q ${CENTER_X} ${JUNCTION_HORIZ_Y} ${CENTER_X - RADIUS} ${JUNCTION_HORIZ_Y} L ${LEFT_LANE_X} ${JUNCTION_HORIZ_Y} V 150 V 250 V 350 V ${PATH_END_Y} V ${LEFT_PATH_FADE_END_Y}`
const RIGHT_PATH = `M ${CENTER_X} ${JUNCTION_Y} Q ${CENTER_X} ${JUNCTION_HORIZ_Y} ${CENTER_X + RADIUS} ${JUNCTION_HORIZ_Y} L ${RIGHT_LANE_X} ${JUNCTION_HORIZ_Y} V 150 V 250 V 380`

const JOURNEY_LEFT_NODES = [
  { x: LEFT_LANE_X, y: 150, labelKey: 0 },
  { x: LEFT_LANE_X, y: 250, labelKey: 1 },
  { x: LEFT_LANE_X, y: 350, labelKey: 2 },
]
const JOURNEY_RIGHT_NODES = [
  { x: RIGHT_LANE_X, y: 150, labelKey: 0 },
  { x: RIGHT_LANE_X, y: 250, labelKey: 1 },
  { x: RIGHT_LANE_X, y: 350, labelKey: 2 },
]
const VIEWBOX_W = 240
const VIEWBOX_H = 430

const PATH_LEFT_ICONS = [NoSymbolIcon, ArrowTrendingUpIcon, FaceFrownIcon]
const PATH_RIGHT_ICONS = [
  ChatBubbleBottomCenterTextIcon,
  ShieldCheckIcon,
  HeartIcon,
]

interface JourneyStepPayload {
  stepId: string
  label: string
  detail: React.ReactNode
  detailExample?: string
}

function JourneyPathNode({
  Icon,
  label,
  detail,
  detailExample,
  stepId,
  isShadow,
  onHover,
  isHovered,
  labelFirst,
  labelOnLightBg,
  showLabel = true,
}: {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
  detail: string
  detailExample?: string
  stepId: string
  isShadow?: boolean
  onHover?: (payload: JourneyStepPayload) => void
  isHovered?: boolean
  labelFirst?: boolean
  labelOnLightBg?: boolean
  showLabel?: boolean
}) {
  const circle = (
    <div
      className={`relative flex h-[60px] w-[60px] shrink-0 cursor-pointer items-center justify-center rounded-full border-2 shadow-lg transition-all ${
        isShadow
          ? "border-zinc-500 bg-zinc-800 text-zinc-400 hover:border-zinc-400"
          : "border-emerald-700 bg-kkm-green text-white shadow-[0_0_20px_rgba(77,184,154,0.3)] hover:border-emerald-800 hover:bg-emerald-600"
      } ${isHovered ? `ring-2 ring-offset-2 ring-offset-white ${isShadow ? "ring-zinc-400" : "ring-emerald-700"}` : ""}`}
    >
      <Icon className="h-6 w-6" strokeWidth={2} />
    </div>
  )
  const labelEl = (
    <span
      className={`max-w-[110px] text-[11px] font-semibold leading-tight break-words sm:max-w-[140px] sm:text-sm ${
        labelFirst ? "text-right" : "text-left"
      } ${
        labelOnLightBg
          ? "text-zinc-700"
          : isShadow
            ? "text-zinc-400"
            : "text-zinc-200"
      }`}
    >
      {label}
    </span>
  )
  return (
    <div
      className="group relative flex cursor-pointer items-center gap-1 sm:gap-3"
      onClick={() => onHover?.({ stepId, label, detail, detailExample })}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onHover?.({ stepId, label, detail, detailExample })
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={label}
    >
      {labelFirst ? (
        <>
          {showLabel ? labelEl : null}
          {circle}
        </>
      ) : (
        <>
          {circle}
          {showLabel ? labelEl : null}
        </>
      )}
    </div>
  )
}

function SourceLink({
  url,
  label,
  fallbackLabel = "Eurostat",
}: {
  url?: string
  label?: string
  fallbackLabel?: string
}) {
  if (!url) return null

  const sourceText = label ?? fallbackLabel

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline align-super text-xs text-zinc-600 underline decoration-dotted underline-offset-2 hover:text-zinc-800 focus:text-zinc-800 focus:outline-none"
    >
      ({sourceText})
    </a>
  )
}

interface JourneyStep {
  title: string
  description: string
  tooltip?: string
  example?: string
}

function UserJourneyVisual({
  startLabel,
  startDetail,
  startExample,
  startSource,
  leftLabel,
  leftSteps,
  rightLabel,
  rightSteps,
  hoveredStep,
  onHoverStep,
  variant = "darkBg",
  showNodeLabels = true,
  animateOnScroll = true,
}: {
  startLabel: string
  startDetail?: string
  startExample?: string
  startSource?: { url: string; label?: string }
  leftLabel: string
  leftSteps: JourneyStep[]
  rightLabel: string
  rightSteps: JourneyStep[]
  hoveredStep?: JourneyStepPayload | null
  onHoverStep?: (step: JourneyStepPayload) => void
  variant?: "darkBg" | "lightBg"
  showNodeLabels?: boolean
  animateOnScroll?: boolean
}) {
  const onLightBg = variant === "lightBg"
  const containerRef = useRef(null)
  const gradientIdPrefix = useId().replace(/:/g, "")
  const inView = useInView(containerRef, { amount: 0.2, once: true })
  const showAnimatedPaths = animateOnScroll ? inView : true

  const TRUNK_DURATION = 1.2
  const BRANCH_DURATION = 1.8

  const leftShort = leftSteps.slice(0, 3)
  const rightShort = rightSteps.slice(0, 3)

  const renderStartDetail = () => {
    const text = startDetail ?? startLabel
    if (!startSource?.url || typeof text !== "string") return text

    const link = (
      <SourceLink
        url={startSource.url}
        label={startSource.label}
        fallbackLabel="Eurostat"
      />
    )

    if (text.includes(SOURCE_PLACEHOLDER)) {
      const [before, ...rest] = text.split(SOURCE_PLACEHOLDER)
      const after = rest.join(SOURCE_PLACEHOLDER)
      return (
        <>
          {before}
          {link}
          {after}
        </>
      )
    }

    const needle = "korkein."
    if (!text.includes(needle)) {
      return (
        <>
          {text} {link}
        </>
      )
    }

    const [before, ...rest] = text.split(needle)
    const after = rest.join(needle)
    return (
      <>
        {before}
        {needle} {link} {after}
      </>
    )
  }

  return (
    <div
      ref={containerRef}
      className="relative flex w-full max-w-xl flex-col items-center"
    >
      <div className="relative min-h-[430px] w-full pb-4 pt-2">
        {/* Background: straight SVG path, draw animation on scroll */}
        <svg
          viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
          className="absolute inset-0 h-full w-full"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient
              id={`${gradientIdPrefix}-path-right`}
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#4db89a" />
              <stop offset="100%" stopColor="#2dd4bf" />
            </linearGradient>
            <linearGradient
              id={`${gradientIdPrefix}-path-trunk`}
              gradientUnits="userSpaceOnUse"
              x1={CENTER_X}
              y1={NODE_BOTTOM_Y}
              x2={CENTER_X}
              y2={JUNCTION_Y}
            >
              <stop offset="0%" stopColor="#71717a" />
              <stop offset="100%" stopColor="#4db89a" />
            </linearGradient>
            <linearGradient
              id={`${gradientIdPrefix}-path-left-fade`}
              x1="0"
              x2="0"
              y1="0"
              y2="1"
              gradientUnits="objectBoundingBox"
            >
              <stop offset="0" stopColor="#52525b" stopOpacity="1" />
              <stop offset="0.74" stopColor="#52525b" stopOpacity="1" />
              <stop offset="0.86" stopColor="#52525b" stopOpacity="0.6" />
              <stop offset="0.94" stopColor="#52525b" stopOpacity="0.2" />
              <stop offset="1" stopColor="#52525b" stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.path
            d={TRUNK_PATH}
            fill="none"
            stroke={`url(#${gradientIdPrefix}-path-trunk)`}
            strokeWidth={PATH_STROKE}
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{
              pathLength: showAnimatedPaths ? 1 : 0,
              opacity: showAnimatedPaths ? 1 : 0,
            }}
            transition={{ duration: TRUNK_DURATION, ease: "linear" }}
            vectorEffect="non-scaling-stroke"
          />
          <motion.path
            d={LEFT_PATH}
            fill="none"
            stroke={`url(#${gradientIdPrefix}-path-left-fade)`}
            strokeWidth={PATH_STROKE}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{
              pathLength: showAnimatedPaths ? 1 : 0,
              opacity: showAnimatedPaths ? 1 : 0,
            }}
            transition={{
              duration: BRANCH_DURATION,
              ease: "linear",
              delay: TRUNK_DURATION,
            }}
            vectorEffect="non-scaling-stroke"
          />
          <motion.path
            d={RIGHT_PATH}
            fill="none"
            stroke={`url(#${gradientIdPrefix}-path-right)`}
            strokeWidth={PATH_STROKE}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{
              pathLength: showAnimatedPaths ? 1 : 0,
              opacity: showAnimatedPaths ? 1 : 0,
            }}
            transition={{
              duration: BRANCH_DURATION,
              ease: "linear",
              delay: TRUNK_DURATION,
            }}
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* Start node (shared) – circle on path, label to the left; click shows section box like other nodes */}
        <div
          className="absolute left-0 right-0 top-0 h-[60px] cursor-pointer"
          onClick={() =>
            onHoverStep?.({
              stepId: "start",
              label: startLabel,
              detail: renderStartDetail(),
              detailExample: startExample,
            })
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onHoverStep?.({
                stepId: "start",
                label: startLabel,
                detail: renderStartDetail(),
                detailExample: startExample,
              })
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={startLabel}
        >
          <div
            className={`absolute left-1/2 top-0 flex h-[60px] w-[60px] -translate-x-1/2 items-center justify-center rounded-full border-2 border-amber-600/70 bg-kkm-yellow text-zinc-800 shadow-lg transition-all ${
              hoveredStep?.label === startLabel
                ? "ring-2 ring-amber-600 ring-offset-2 ring-offset-white"
                : ""
            }`}
          >
            <ExclamationTriangleIcon className="h-6 w-6" strokeWidth={2} />
          </div>
          <span
            className={`absolute right-[calc(50%+30px+0.45rem)] top-1/2 w-[96px] -translate-y-1/2 text-right text-[11px] font-semibold leading-tight sm:right-[calc(50%+30px+0.7rem)] sm:w-[160px] sm:text-sm ${onLightBg ? "text-zinc-600" : "text-zinc-400"}`}
          >
            {startLabel}
          </span>
        </div>

        {/* Left path nodes – [text][icon], icon centered on path line */}
        {JOURNEY_LEFT_NODES.map((pos, i) => {
          const step = leftShort[i]
          const Icon = PATH_LEFT_ICONS[i]
          if (!step || !Icon) return null
          const rightPct = ((VIEWBOX_W - pos.x) / VIEWBOX_W) * 100
          const topPct = (pos.y / VIEWBOX_H) * 100
          return (
            <div
              key={`left-${i}`}
              className="absolute flex -translate-y-1/2 items-center justify-end"
              style={{
                right: `${rightPct}%`,
                top: `${topPct}%`,
                transform: "translate(30px, -50%)",
              }}
            >
              <JourneyPathNode
                Icon={Icon}
                label={step.title}
                detail={step.tooltip ?? step.description}
                detailExample={step.example}
                stepId={`left-${i}`}
                isShadow
                onHover={(payload) => onHoverStep?.(payload)}
                isHovered={hoveredStep?.label === step.title}
                labelFirst
                labelOnLightBg={onLightBg}
                showLabel={showNodeLabels}
              />
            </div>
          )
        })}

        {/* Right path nodes – [icon][text], text to the right of the node; -30px centers 60px icon on path */}
        {JOURNEY_RIGHT_NODES.map((pos, i) => {
          const step = rightShort[i]
          const Icon = PATH_RIGHT_ICONS[i]
          if (!step || !Icon) return null
          const leftPct = (pos.x / VIEWBOX_W) * 100
          const topPct = (pos.y / VIEWBOX_H) * 100
          return (
            <div
              key={`right-${i}`}
              className="absolute flex -translate-y-1/2 items-center justify-start gap-3"
              style={{
                left: `${leftPct}%`,
                top: `${topPct}%`,
                transform: "translate(-30px, -50%)",
              }}
            >
              <JourneyPathNode
                Icon={Icon}
                label={step.title}
                detail={step.tooltip ?? step.description}
                detailExample={step.example}
                stepId={`right-${i}`}
                isShadow={false}
                onHover={(payload) => onHoverStep?.(payload)}
                isHovered={hoveredStep?.label === step.title}
                labelFirst={false}
                labelOnLightBg={onLightBg}
                showLabel={showNodeLabels}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ReportContentColumn({
  children,
  className = "",
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`flex w-full max-w-full flex-1 flex-col justify-center px-2 text-left sm:px-3 lg:min-w-0 lg:px-12 ${className}`.trim()}
      style={REPORT_CONTENT_STYLE}
    >
      {children}
    </div>
  )
}

const REPORT_TEXT_PADDING = "px-8 sm:px-10 lg:px-12"
function ReportTextColumn({
  children,
  className = "",
  inRow = false,
  mediaOnLeft = false,
}: {
  children: React.ReactNode
  className?: string
  inRow?: boolean
  mediaOnLeft?: boolean
}) {
  const base = `flex flex-col justify-center text-left ${REPORT_TEXT_PADDING}`
  if (mediaOnLeft) {
    return (
      <div
        className={`${base} min-w-0 flex-1 ${className}`}
        style={REPORT_CONTENT_STYLE}
      >
        {children}
      </div>
    )
  }
  if (inRow) {
    return (
      <div
        className={`${base} report-inrow-ml min-w-0 flex-1 ${className}`}
        style={REPORT_CONTENT_STYLE}
      >
        {children}
      </div>
    )
  }
  return (
    <div
      className={`${base} mx-auto w-full ${className}`}
      style={REPORT_CONTENT_STYLE}
    >
      {children}
    </div>
  )
}

/** Reusable 3-layer section shell: <section bg> → <div mx-auto max-w> → <div row>. */
function ReportSection({
  bg,
  children,
  id,
  clipOverflow = true,
  rowClassName,
  sectionClassName,
}: {
  bg: string
  children: React.ReactNode
  id?: string
  clipOverflow?: boolean
  rowClassName?: string
  sectionClassName?: string
}) {
  return (
    <section
      id={id}
      className={`${bg}${clipOverflow ? " overflow-x-clip" : ""} ${sectionClassName ?? ""}`.trim()}
    >
      <div className={`mx-auto max-w-[100rem] ${REPORT_SECTION_PADDING}`}>
        <div className={rowClassName ?? REPORT_ROW_CLASS}>{children}</div>
      </div>
    </section>
  )
}

/** Flush-right image column: bleed-right with rounded-l-full mask. */
function FlushRightImage({
  children,
  className = "",
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`report-flush-right order-first flex min-h-[18rem] w-full shrink-0 items-center sm:min-h-[22rem] lg:order-none lg:min-h-[36rem] lg:w-[50vw] ${className}`.trim()}
    >
      <div className="h-full w-full overflow-hidden rounded-l-full">
        {children}
      </div>
    </div>
  )
}

/** Flush-left image column: bleed-left with rounded-r-full mask. */
function FlushLeftImage({
  children,
  className = "",
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`report-flush-left order-first flex min-h-[18rem] w-full shrink-0 items-center sm:min-h-[22rem] lg:min-h-[36rem] lg:w-[50vw] ${className}`.trim()}
    >
      <div className="h-full w-full overflow-hidden rounded-r-full">
        {children}
      </div>
    </div>
  )
}

/** Pie-chart / media column used in multiple report sections. */
function ReportMediaColumn({
  children,
  side = "left",
  className = "",
}: {
  children: React.ReactNode
  side?: "left" | "right"
  className?: string
}) {
  const padding =
    side === "right" ? "pr-8 lg:pr-12" : "pl-8 pr-8 lg:pl-12 lg:pr-12"
  return (
    <div
      className={`w-full shrink-0 lg:w-[380px] xl:w-[440px] ${padding}${className ? ` ${className}` : ""}`}
    >
      {children}
    </div>
  )
}

/** AI section: clickable tiles with scroll animation and click-to-show tooltip. Tiles use platform-box style; tooltip appears below grid. */
const aiTilesGridVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
}
const aiTileItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
}
const feedbackQuotesGridVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.14, delayChildren: 0.12 },
  },
}
const swedenPillsContainerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
}
const swedenPillItemVariants = {
  hidden: { opacity: 0, y: 14, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.45, ease: "easeOut" },
  },
}
const sextortionOverlayVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.24, delayChildren: 0.2 },
  },
}
const sextortionOverlayItemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
}
const feedbackQuoteOverlayPositions = [
  "left-[15%] top-[3%] w-[74%] sm:w-[50%] lg:w-[34%]",
  "left-[10%] bottom-[2%] md:right-[10%] md:top-[9%] md:left-auto md:bottom-auto sm:hidden lg:block w-[74%] md:w-[50%] lg:w-[25%]",
  "left-[30%] top-[50%] hidden w-[32%] lg:block",
  "left-[4%] top-[31%] hidden w-[33%] md:block",
  "right-[5%] lg:right-[15%] -bottom-[5%] lg:bottom-[5%] hidden w-[30%] sm:block",
  "left-[5%] bottom-[2%] hidden w-[50%] sm:block",
]
interface AICaseTile {
  id: string
  tooltipTitle: string
  tooltipQuote?: string
  tooltipAttribution?: string
  tooltipDisclaimer?: string
  tooltipBody?: string
}
function AICaseTilesSection({
  id,
  tiles,
  heading,
  text1,
  text2,
  clickHint,
  openAriaSuffix,
}: {
  id: string
  tiles: AICaseTile[]
  heading: string
  text1: string
  text2: string
  clickHint: string
  openAriaSuffix: string
}) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const interactionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { amount: 0.15, once: true })
  const [activeTileId, setActiveTileId] = useState<string | null>(null)
  const [hoveredTileId, setHoveredTileId] = useState<string | null>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveTileId(null)
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        interactionRef.current &&
        !interactionRef.current.contains(e.target as Node)
      )
        setActiveTileId(null)
    }
    document.addEventListener("keydown", handleEscape)
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const activeTile = tiles?.find((t) => t.id === activeTileId)

  return (
    <section id={id} className={`${REPORT_BLACK_BG} report-anchor`}>
      <div className={`mx-auto max-w-[100rem] ${REPORT_SECTION_PADDING}`}>
        <div
          ref={interactionRef}
          className="flex w-full flex-col items-center gap-6 lg:flex-row lg:items-center lg:gap-4"
        >
          <div
            ref={sectionRef}
            className="w-full shrink-0 pl-8 pr-4 lg:w-[520px] lg:pl-12 lg:pr-6 xl:w-[580px]"
          >
            <p className="mb-3 flex items-center gap-2 text-base text-zinc-500">
              <HandRaisedIcon
                className="h-4 w-4 shrink-0 text-kkm-green"
                aria-hidden
              />
              <span>{clickHint}</span>
            </p>
            <motion.div
              className="grid grid-cols-3 gap-3 sm:gap-4"
              variants={aiTilesGridVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              {tiles?.map((tile) => {
                const isGrayedOut =
                  hoveredTileId != null && hoveredTileId !== tile.id
                return (
                  <motion.div
                    key={tile.id}
                    variants={aiTileItemVariants}
                    className="min-w-0 transition-[opacity,filter] duration-200"
                    style={{
                      opacity: isGrayedOut ? 0.4 : 1,
                      filter: isGrayedOut
                        ? "grayscale(0.6) brightness(0.85)"
                        : "none",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveTileId(tile.id)}
                      onMouseEnter={() => setHoveredTileId(tile.id)}
                      onMouseLeave={() => setHoveredTileId(null)}
                      className={`relative flex h-[24rem] w-full min-w-0 flex-col overflow-hidden rounded-xl border-2 p-1 transition-all sm:h-[28rem] sm:p-1.5 ${
                        activeTileId === tile.id
                          ? "border-kkm-green/70 ring-2 ring-kkm-green/50 ring-offset-2 ring-offset-zinc-700"
                          : "border-zinc-600/60 hover:border-zinc-500"
                      }`}
                      aria-expanded={activeTileId === tile.id}
                      aria-label={`${tile.tooltipTitle}. ${openAriaSuffix}`}
                    >
                      <div className="relative h-full min-h-0 w-full overflow-hidden rounded-lg bg-zinc-800">
                        {tile.id === "sextortion" ? (
                          <StaticImage
                            src="../../../images/annual-reports/ai1.jpg"
                            alt=""
                            layout="fullWidth"
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        ) : tile.id === "kiusaaminen" ? (
                          <StaticImage
                            src="../../../images/annual-reports/ai2.jpg"
                            alt=""
                            layout="fullWidth"
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        ) : (
                          <StaticImage
                            src="../../../images/annual-reports/ai3.jpg"
                            alt=""
                            layout="fullWidth"
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/90 via-transparent to-transparent" />
                        {/* Long compounds that do not fit this narrow column are broken
                            by `body { word-wrap: break-word }` at an arbitrary character,
                            which reads as a spelling error. `hyphens: auto` fixes the
                            grammar but over-hyphenates the other labels, so the break
                            points are authored as soft hyphens (JSON "\u00ad") in the
                            translations instead — see aiCaseTiles in fi-fi-default.json. */}
                        <span className="absolute bottom-2 left-1.5 right-1.5 text-left text-xs font-semibold leading-tight text-white drop-shadow-md sm:bottom-3 sm:text-sm">
                          {tile.tooltipTitle}
                        </span>
                      </div>
                    </button>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
          <ReportContentColumn className="lg:max-w-[44%] xl:max-w-[40%]">
            <h2 className="text-2xl font-bold tracking-tight text-zinc-100 sm:text-3xl">
              {heading}
            </h2>
            <div className="relative mt-2">
              <p className="text-lg leading-relaxed text-zinc-400">{text1}</p>
              {text2 ? (
                <p className="mt-4 text-lg leading-relaxed text-zinc-400">
                  {text2}
                </p>
              ) : null}
              {activeTile && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-x-0 bottom-5 top-0 hidden overflow-y-auto rounded-xl border border-zinc-600/80 bg-zinc-800 p-5 pr-12 shadow-lg ring-1 ring-zinc-700/50 sm:block"
                  role="dialog"
                  aria-label="Esimerkkitapaus"
                >
                  <button
                    type="button"
                    onClick={() => setActiveTileId(null)}
                    className="absolute right-3 top-3 rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-700/80 hover:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-kkm-green/50"
                    aria-label="Sulje"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                  <h4 className="text-base font-semibold text-kkm-green">
                    {activeTile.tooltipTitle}
                  </h4>
                  {activeTile.tooltipQuote != null ? (
                    <>
                      <blockquote className="relative mt-4 pl-6 pr-4 text-base leading-relaxed text-zinc-200">
                        <span
                          className="pointer-events-none absolute -left-0.5 -top-2 text-4xl font-semibold leading-none text-zinc-500"
                          aria-hidden
                        >
                          "
                        </span>
                        {activeTile.tooltipQuote}
                      </blockquote>
                      {activeTile.tooltipAttribution && (
                        <p className="mt-3 pl-6 text-base font-medium text-zinc-400">
                          — {activeTile.tooltipAttribution}
                        </p>
                      )}
                      {activeTile.tooltipDisclaimer && (
                        <p className="mt-2 pl-6 text-sm italic text-zinc-500">
                          {activeTile.tooltipDisclaimer}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="mt-2 text-base leading-relaxed text-zinc-300">
                      {activeTile.tooltipBody}
                    </p>
                  )}
                </motion.div>
              )}
              <MobileBottomSheet
                open={!!activeTile}
                onClose={() => setActiveTileId(null)}
                ariaLabel="Esimerkkitapaus"
                variant="dark"
              >
                {activeTile && (
                  <>
                    <h4 className="text-xl font-semibold text-kkm-green">
                      {activeTile.tooltipTitle}
                    </h4>
                    {activeTile.tooltipQuote != null ? (
                      <>
                        <blockquote className="relative mt-4 pl-6 pr-4 text-base leading-relaxed text-zinc-200">
                          <span
                            className="pointer-events-none absolute -left-0.5 -top-2 text-4xl font-semibold leading-none text-zinc-500"
                            aria-hidden
                          >
                            "
                          </span>
                          {activeTile.tooltipQuote}
                        </blockquote>
                        {activeTile.tooltipAttribution && (
                          <p className="mt-3 pl-6 text-base font-medium text-zinc-400">
                            — {activeTile.tooltipAttribution}
                          </p>
                        )}
                        {activeTile.tooltipDisclaimer && (
                          <p className="mt-2 pl-6 text-sm italic text-zinc-500">
                            {activeTile.tooltipDisclaimer}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="mt-2 text-base leading-relaxed text-zinc-300">
                        {activeTile.tooltipBody}
                      </p>
                    )}
                  </>
                )}
              </MobileBottomSheet>
            </div>
          </ReportContentColumn>
        </div>
      </div>
    </section>
  )
}

/** Horizontal bar chart (psychological symptoms). Renders only on client; animates when in view. */
function HorizontalSymptomsBarChartPlaceholder({
  title,
  chartData,
  dark,
  nameKey = "name",
  valueKey = "value",
  valueSuffix = "%",
}: {
  title: string
  chartData: Array<Record<string, number | string>>
  dark: boolean
  nameKey?: string
  valueKey?: string
  valueSuffix?: string
}) {
  const [mounted, setMounted] = useState(false)
  const [chartInView, setChartInView] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const hasAnimatedRef = useRef(false)

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!mounted || !containerRef.current || hasAnimatedRef.current) return
    const el = containerRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasAnimatedRef.current) return
        hasAnimatedRef.current = true
        observer.disconnect()
        setChartInView(true)
      },
      { threshold: 0.5, rootMargin: "0px" },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [mounted])

  useEffect(() => {
    if (!mounted) return
    const media = window.matchMedia("(max-width: 639px)")
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches)
    setIsMobile(media.matches)
    media.addEventListener("change", onChange)
    return () => media.removeEventListener("change", onChange)
  }, [mounted])

  if (!mounted) {
    return <div className="h-full min-h-[20rem] w-full" aria-hidden />
  }

  const tickFill = dark ? "#e4e4e7" : "#27272a"
  const axisStroke = dark ? "#71717a" : "#52525b"
  const tickStyle = { fontSize: 13, fontWeight: 600 }
  const yAxisWidth = isMobile ? 168 : 220
  const chartMargin = { top: 8, right: isMobile ? 46 : 56, left: 0, bottom: 8 }
  const barCategoryGap = isMobile ? 12 : 8
  const pctSuffix = valueSuffix === "%" ? PCT_SUFFIX_FI : valueSuffix
  const formatValue = (v: number | string) => {
    if (typeof v !== "number") return String(v) + pctSuffix
    const raw = String(v)
    const decimalsFromInput = raw.includes(".")
      ? raw.split(".")[1]?.length ?? 0
      : 0
    const decimals = Math.min(3, decimalsFromInput)
    const formatted = v.toLocaleString("en-US", {
      useGrouping: false,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    return `${formatted}${pctSuffix}`
  }

  const renderAnimatedBarLabel = (props: {
    x?: number | string
    y?: number | string
    width?: number | string
    height?: number | string
    value?: number | string
    index?: number
  }) => {
    const { x, y, width, height, value, index } = props
    const numericX = Number(x)
    const numericWidth = Number(width)
    const numericY = Number(y)
    const numericHeight = Number(height)

    if (
      !Number.isFinite(numericX) ||
      !Number.isFinite(numericWidth) ||
      !Number.isFinite(numericY) ||
      !Number.isFinite(numericHeight)
    ) {
      return null
    }

    return (
      <motion.text
        x={numericX + numericWidth + 8}
        y={numericY + numericHeight / 2}
        dy="0.35em"
        fill={tickFill}
        fontSize={13}
        fontWeight={700}
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{
          duration: 1.5,
          ease: "easeOut",
          delay: (index ?? 0) * 0.06,
        }}
      >
        {formatValue(value)}
      </motion.text>
    )
  }

  const wrapTickText = (value: number | string) => {
    const label = String(value ?? "")
    if (!isMobile) return [label]
    const maxCharsPerLine = 21
    const words = label.split(/\s+/).filter(Boolean)
    const lines: string[] = []
    let currentLine = ""

    words.forEach((word) => {
      const candidate = currentLine ? `${currentLine} ${word}` : word
      if (candidate.length <= maxCharsPerLine) {
        currentLine = candidate
        return
      }
      if (currentLine) lines.push(currentLine)
      currentLine = word
    })

    if (currentLine) lines.push(currentLine)
    return lines.length > 0 ? lines : [label]
  }

  const renderCategoryTick = (props: {
    x?: number
    y?: number
    payload?: { value: string | number }
  }) => {
    const { x, y, payload } = props
    const lines = wrapTickText(payload?.value)
    const lineHeight = 13
    const offsetY = ((lines.length - 1) * lineHeight) / 2

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={-8}
          y={0}
          textAnchor="end"
          fill={tickFill}
          fontSize={12}
          fontWeight={600}
          dominantBaseline="middle"
        >
          {lines.map((line: string, idx: number) => (
            <tspan
              key={`${line}-${idx}`}
              x={-8}
              dy={idx === 0 ? -offsetY : lineHeight}
            >
              {line}
            </tspan>
          ))}
        </text>
      </g>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`h-full w-full ${isMobile ? "min-h-[30rem]" : "min-h-[20rem]"}`}
    >
      {chartInView && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="h-full w-full"
        >
          <p className="mb-3 text-sm font-semibold text-zinc-300">{title}</p>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={chartData}
              margin={chartMargin}
              barCategoryGap={barCategoryGap}
            >
              <XAxis
                type="number"
                domain={[0, 60]}
                tick={{ fill: tickFill, ...tickStyle }}
                axisLine={{ stroke: axisStroke, strokeWidth: 2 }}
                tickLine={{ stroke: axisStroke, strokeWidth: 1 }}
                tickFormatter={formatValue}
              />
              <YAxis
                type="category"
                dataKey={nameKey}
                width={yAxisWidth}
                tick={
                  isMobile
                    ? renderCategoryTick
                    : { fill: tickFill, ...tickStyle }
                }
                axisLine={{ stroke: axisStroke, strokeWidth: 2 }}
                tickLine={{ stroke: axisStroke, strokeWidth: 1 }}
              />
              <Tooltip
                cursor={false}
                formatter={(value: number) => [formatValue(value), "Osuus"]}
                contentStyle={{
                  backgroundColor: dark ? "#3f3f46" : "#27272a",
                  border: "none",
                  borderRadius: "8px",
                  color: "#ffffff",
                }}
                labelStyle={{ color: "#ffffff" }}
              />
              <Bar
                dataKey={valueKey}
                fill="#3a9a7a"
                activeBar={{ fill: "#72c9ac" }}
                radius={[0, 6, 6, 0]}
                isAnimationActive
                animationBegin={0}
                animationDuration={1500}
              >
                <LabelList
                  dataKey={valueKey}
                  content={renderAnimatedBarLabel}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      )}
    </div>
  )
}

const CRIME_TOOLTIP_SPACE_NEEDED = 320
const CRIME_TOOLTIP_WIDTH = 384 // max-w-sm
const CRIME_TOOLTIP_HALF_WIDTH = CRIME_TOOLTIP_WIDTH / 2
const CRIME_TOOLTIP_VIEWPORT_PADDING = 24 // keep consistent side gutter on mobile
const CRIME_TOOLTIP_FALLBACK_TEXT = "Lisätietoa ei saatavilla tällä hetkellä."
const CRIME_TAG_KEY_DEFAMATION = "Kunnianloukkaus"
const CRIME_TAG_KEY_SEXUAL_CRIMES =
  "Lapsen seksuaalista itsemääräämisoikeutta loukkaavat rikokset"
const CRIME_TAG_KEY_ILLEGAL_THREAT = "Laiton uhkaus"
const normalizeCrimeTagKey = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()

/** Grey rounded box listing crime tags with percentage and emoji. Uses latest year from data. */
function CrimeTagsBox({
  data,
  title,
  clickHint,
  indexNote,
  emojiMap,
  tooltipMap,
  dark = true,
}) {
  const [mounted, setMounted] = useState(false)
  const [boxInView, setBoxInView] = useState(false)
  const [showIndexNote, setShowIndexNote] = useState(false)
  const [hoveredTag, setHoveredTag] = useState<string | null>(null)
  const [tappedTag, setTappedTag] = useState<string | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number
    top?: number
    bottom?: number
    placement: "top" | "bottom"
  } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleTagMouseEnter = (
    e: React.MouseEvent<HTMLElement>,
    tag: string,
  ) => {
    if (!tooltipMap?.[tag]) return
    const rect = e.currentTarget.getBoundingClientRect()
    if (typeof window === "undefined") {
      setHoveredTag(tag)
      return
    }
    const winW = window.innerWidth
    const winH = window.innerHeight
    const spaceBelow = winH - rect.bottom
    const spaceAbove = rect.top
    const placement =
      spaceBelow >= CRIME_TOOLTIP_SPACE_NEEDED
        ? "bottom"
        : spaceAbove >= CRIME_TOOLTIP_SPACE_NEEDED
          ? "top"
          : "bottom"
    const centerX = rect.left + rect.width / 2
    const left = Math.max(
      CRIME_TOOLTIP_VIEWPORT_PADDING,
      Math.min(
        centerX - CRIME_TOOLTIP_HALF_WIDTH,
        winW - CRIME_TOOLTIP_VIEWPORT_PADDING - CRIME_TOOLTIP_WIDTH,
      ),
    )
    const top = placement === "bottom" ? rect.bottom + 8 : undefined
    const bottom = placement === "top" ? winH - rect.top + 8 : undefined
    setTooltipPosition({ left, top, bottom, placement })
    setHoveredTag(tag)
  }

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!mounted || !containerRef.current) return
    const el = containerRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setBoxInView(true)
      },
      { threshold: 0.5, rootMargin: "0px" },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [mounted])

  useEffect(() => {
    if (!boxInView) return
    const t = setTimeout(() => setShowIndexNote(true), 800)
    return () => clearTimeout(t)
  }, [boxInView])

  if (!data?.length) return null
  const latest = data[data.length - 1]
  const allKeys = Object.keys(latest).filter((k) => k !== "year")
  // Sexual crimes first, then the rest in data order
  const SEXUAL_CRIMES_KEY =
    "Lapsen seksuaalista itsemääräämisoikeutta loukkaavat rikokset"
  const tags = allKeys.includes(SEXUAL_CRIMES_KEY)
    ? [SEXUAL_CRIMES_KEY, ...allKeys.filter((k) => k !== SEXUAL_CRIMES_KEY)]
    : allKeys
  const tooltipEntries =
    tooltipMap && typeof tooltipMap === "object"
      ? Object.entries(tooltipMap).filter(
          ([, value]) => typeof value === "string" && value.trim().length > 0,
        )
      : []
  const normalizedTooltipMap = new Map(
    tooltipEntries.map(([key, value]) => [
      normalizeCrimeTagKey(key),
      value as string,
    ]),
  )
  const orderedTooltipValues = tooltipEntries.map(([, value]) => value as string)
  const resolvedTooltipByTag = tags.reduce<Record<string, string>>((acc, tag, i) => {
    const exactTooltip =
      typeof tooltipMap?.[tag] === "string" && tooltipMap[tag].trim().length > 0
        ? tooltipMap[tag]
        : undefined
    const normalizedTooltip = normalizedTooltipMap.get(normalizeCrimeTagKey(tag))
    const orderedFallbackTooltip = orderedTooltipValues[i]
    const firstAvailableTooltip = orderedTooltipValues[0]
    const tooltip =
      exactTooltip ??
      normalizedTooltip ??
      orderedFallbackTooltip ??
      firstAvailableTooltip ??
      CRIME_TOOLTIP_FALLBACK_TEXT
    if (typeof tooltip === "string" && tooltip.trim().length > 0) acc[tag] = tooltip
    return acc
  }, {})
  const boxClass = dark
    ? "rounded-2xl bg-zinc-900/75 ring-1 ring-zinc-600/70 px-5 py-5 sm:px-6 sm:py-6"
    : "rounded-2xl bg-zinc-200 px-5 py-5 sm:px-6 sm:py-6"
  const textClass = dark ? "text-zinc-200" : "text-zinc-800"
  const subTextClass = dark ? "text-zinc-400" : "text-zinc-600"

  return (
    <div
      ref={containerRef}
      className="flex min-h-[20rem] w-full flex-col lg:h-full"
    >
      {title && (
        <p
          className={`mb-3 text-left text-base font-semibold transition-all duration-[1500ms] ease-out ${subTextClass} ${boxInView ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}
        >
          {title}
        </p>
      )}
      {clickHint && (
        <p
          className={`mb-3 flex items-start gap-2 text-left text-sm leading-relaxed transition-all duration-[1500ms] ease-out ${subTextClass} ${boxInView ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}
        >
          <HandRaisedIcon
            className="mt-0.5 h-4 w-4 shrink-0 text-kkm-green"
            aria-hidden
          />
          {clickHint}
        </p>
      )}
      <div
        className={`transition-all duration-[1500ms] ease-out ${boxClass} ${boxInView ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}
      >
        <ul className="flex flex-col gap-4 sm:gap-5" aria-label={title}>
          {tags.map((tag, i) => {
            const hasTooltip = !!resolvedTooltipByTag[tag]
            const isHovered = hoveredTag === tag
            return (
              <li
                key={tag}
                className={`relative transition-all duration-[1500ms] ease-out ${boxInView ? "translate-x-0 opacity-100" : "translate-x-3 opacity-0"}`}
                style={
                  boxInView
                    ? { transitionDelay: `${200 + i * 200}ms` }
                    : undefined
                }
              >
                <div
                  className="relative"
                  onMouseEnter={(e) =>
                    hasTooltip && handleTagMouseEnter(e, tag)
                  }
                  onMouseLeave={() => {
                    setHoveredTag(null)
                    setTooltipPosition(null)
                  }}
                >
                  <div
                    role={hasTooltip ? "button" : undefined}
                    tabIndex={hasTooltip ? 0 : undefined}
                    onClick={() => hasTooltip && setTappedTag(tag)}
                    aria-label={
                      hasTooltip
                        ? `${tag}: ${latest[tag]} %. Hover for details.`
                        : undefined
                    }
                    className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2.5 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-700 sm:gap-4 sm:px-3 sm:py-3 ${dark ? "bg-zinc-800/60 hover:bg-zinc-700/70" : "bg-white/80 hover:bg-zinc-100"} ${hasTooltip ? "cursor-pointer" : "cursor-default"} ${isHovered ? "ring-2 ring-kkm-green/50" : ""}`}
                  >
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center text-xl sm:h-12 sm:w-12 sm:text-3xl"
                      aria-hidden
                    >
                      {emojiMap?.[tag] ?? "•"}
                    </span>
                    <span
                      className={`min-w-0 flex-1 hyphens-none whitespace-normal break-normal text-sm font-medium [overflow-wrap:normal] sm:text-base ${textClass}`}
                    >
                      {tag}
                    </span>
                    <span
                      className={`shrink-0 font-semibold tabular-nums ${textClass}`}
                    >
                      {latest[tag]} %
                    </span>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
      {indexNote && (
        <p
          className={`mt-3 text-left text-xs leading-snug transition-all duration-[1500ms] ease-out ${subTextClass} ${showIndexNote ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}
        >
          {indexNote}
        </p>
      )}

      {/* Tooltip in portal so it is never clipped and stays on top */}
      {mounted &&
        hoveredTag &&
        tooltipPosition &&
        resolvedTooltipByTag[hoveredTag] &&
        typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            <motion.div
              key={hoveredTag}
              role="tooltip"
              className="fixed z-[9999] w-[calc(100vw-3rem)] max-w-sm"
              style={{
                left: tooltipPosition.left,
                top: tooltipPosition.top,
                bottom: tooltipPosition.bottom,
              }}
              initial={{
                opacity: 0,
                y: tooltipPosition.placement === "bottom" ? -6 : 6,
              }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: tooltipPosition.placement === "bottom" ? -6 : 6,
              }}
              transition={{ duration: 0.15 }}
            >
              <div className="relative rounded-xl border border-zinc-600/80 bg-zinc-800/95 p-4 shadow-xl ring-1 ring-zinc-700/50 backdrop-blur sm:p-5">
                <div
                  className={`absolute left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-zinc-600/80 bg-zinc-800/95 ${tooltipPosition.placement === "bottom" ? "-top-2 border-l border-t" : "-bottom-2 border-b border-r"}`}
                  aria-hidden
                />
                <div className="relative">
                  <p className="text-sm leading-relaxed text-zinc-300">
                    {resolvedTooltipByTag[hoveredTag]}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body,
        )}

      <MobileBottomSheet
        open={!!tappedTag && !!(tappedTag && resolvedTooltipByTag[tappedTag])}
        onClose={() => setTappedTag(null)}
        ariaLabel="Rikosnimikkeen kuvaus"
        variant="dark"
      >
        {tappedTag && resolvedTooltipByTag[tappedTag] && (
          <>
            <p className="text-xl font-semibold text-kkm-green">
              {emojiMap?.[tappedTag] ? `${emojiMap[tappedTag]} ` : ""}
              {tappedTag}
            </p>
            <p className="mt-3 text-base leading-relaxed text-zinc-300">
              {resolvedTooltipByTag[tappedTag]}
            </p>
          </>
        )}
      </MobileBottomSheet>
    </div>
  )
}

/** Pie chart (educator / told-adult data). Renders only on client; animates when in view (once). Optional title above. dark=false for light backgrounds. Uses visible segment borders and readable text for accessibility across all report pie charts. */
function TeacherPieChartPlaceholder({
  pieData,
  title,
  dark = true,
  bolderLabels = false,
}) {
  const [mounted, setMounted] = useState(false)
  const [chartInView, setChartInView] = useState(false)
  const containerRef = useRef(null)
  const hasAnimatedRef = useRef(false)
  const titleWeightClass = bolderLabels ? "font-semibold" : "font-medium"
  const legendWeightClass = bolderLabels ? "font-semibold" : "font-medium"

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!mounted || !containerRef.current || hasAnimatedRef.current) return
    const el = containerRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasAnimatedRef.current) return
        hasAnimatedRef.current = true
        observer.disconnect()
        setChartInView(true)
      },
      { threshold: 0.5, rootMargin: "0px" },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [mounted])

  if (!mounted) {
    return <div className="h-full min-h-[16rem] w-full" aria-hidden />
  }

  const labelFill = dark ? "#fafafa" : "#27272a"
  const titleClass = dark ? "text-zinc-300" : "text-zinc-700"
  const legendClass = dark ? "text-zinc-300" : "text-zinc-700"
  const tooltipStyle = dark
    ? {
        backgroundColor: "#3f3f46",
        border: "none",
        borderRadius: "8px",
        color: "#fafafa",
      }
    : {
        backgroundColor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "8px",
        color: "#27272a",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }

  const strokeColor = "#e4e4e7"
  const strokeWidth = 2
  const labelFontSize = 16
  const labelFontWeight = 600

  return (
    <div
      ref={containerRef}
      className="flex h-full min-h-[16rem] w-full flex-col"
    >
      {title && (
        <p
          className={`mb-2 text-center text-base ${titleWeightClass} ${titleClass}`}
        >
          {title}
        </p>
      )}
      <div className="min-h-0 flex-1">
        {chartInView && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="h-full w-full"
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius="90%"
                  dataKey="value"
                  isAnimationActive
                  animationBegin={0}
                  animationDuration={1500}
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  label={({ cx, cy, midAngle, outerRadius, percent }) => {
                    const RADIAN = Math.PI / 180
                    const radius = outerRadius * 0.65
                    const x = cx + radius * Math.cos(-midAngle * RADIAN)
                    const y = cy + radius * Math.sin(-midAngle * RADIAN)
                    return (
                      <text
                        x={x}
                        y={y}
                        fill={labelFill}
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{
                          fontSize: labelFontSize,
                          fontWeight: labelFontWeight,
                        }}
                      >
                        {`${(percent * 100).toFixed(1)}${PCT_SUFFIX_FI}`}
                      </text>
                    )
                  }}
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.fill}
                      stroke={strokeColor}
                      strokeWidth={strokeWidth}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={tooltipStyle}
                  itemStyle={{ color: dark ? "#fafafa" : "#27272a" }}
                  labelStyle={{ color: dark ? "#e4e4e7" : "#52525b" }}
                  formatter={(value, name) => [
                    `${value}${PCT_SUFFIX_FI}`,
                    name,
                  ]}
                />
                <Legend
                  wrapperStyle={{ paddingTop: "12px" }}
                  formatter={(value) => (
                    <span
                      className={`text-base ${legendWeightClass} ${legendClass}`}
                    >
                      {value}
                    </span>
                  )}
                  iconSize={14}
                  iconType="square"
                />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        )}
      </div>
    </div>
  )
}

/** Number that counts up from 0 when the section scrolls into view. Use embedded + variant="light" inside teal section. suffix="+" shows e.g. "5 000+" for "over 5000". */
function CountUpSection({
  target,
  label,
  durationMs = 1800,
  embedded = false,
  variant = "dark",
  suffix = "",
}) {
  const [mounted, setMounted] = useState(false)
  const [displayValue, setDisplayValue] = useState(0)
  const hasAnimatedRef = useRef(false)
  const sectionRef = useRef(null)
  const startTimeRef = useRef(null)
  const rafRef = useRef(null)

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!mounted || !sectionRef.current || typeof target !== "number") return
    const el = sectionRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimatedRef.current) {
          hasAnimatedRef.current = true
          startTimeRef.current = null
          const step = (timestamp) => {
            if (startTimeRef.current == null) startTimeRef.current = timestamp
            const elapsed = timestamp - startTimeRef.current
            const t = Math.min(elapsed / durationMs, 1)
            const quarticEaseOut = 1 - (1 - t) ** 4
            const value = Math.round(quarticEaseOut * target)
            setDisplayValue(value)
            if (t < 1) rafRef.current = requestAnimationFrame(step)
          }
          rafRef.current = requestAnimationFrame(step)
        }
      },
      { threshold: 0.5, rootMargin: "0px" },
    )
    observer.observe(el)
    return () => {
      observer.disconnect()
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [mounted, target, durationMs])

  const isLight = variant === "light"
  const numberClass = isLight ? "text-white" : "text-zinc-100"
  const labelClass = isLight ? "text-white" : "text-zinc-400"

  if (!mounted) {
    if (embedded) {
      return (
        <div
          className="flex min-h-[9rem] items-center justify-center"
          aria-hidden
        />
      )
    }
    return (
      <section
        className={`border-t border-zinc-700/80 ${REPORT_SECTION_PADDING}`}
        aria-hidden
      >
        <div className="flex min-h-[9rem] items-center justify-center" />
      </section>
    )
  }

  const showSuffix = suffix && displayValue >= target
  const formatted =
    displayValue.toLocaleString("fi-FI") + (showSuffix ? suffix : "")
  const finalFormatted = target.toLocaleString("fi-FI") + (suffix || "")
  const numberClasses = `tabular-nums tracking-tight ${numberClass} text-4xl font-black sm:text-5xl lg:text-6xl ${
    isLight
      ? "drop-shadow-[0_2px_12px_rgba(0,0,0,0.15)]"
      : "drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
  }`
  const content = (
    <div className="flex min-h-[9rem] flex-col items-center justify-center gap-3 py-6 text-center">
      <div
        className={`relative flex flex-col items-center justify-center overflow-hidden rounded-xl px-6 py-8 ring-2 sm:px-8 sm:py-10 lg:px-10 lg:py-12 ${
          isLight
            ? "bg-gradient-to-b from-white/30 via-white/15 to-white/5 shadow-[0_8px_32px_rgba(0,0,0,0.12)] ring-white/30"
            : "bg-gradient-to-b from-zinc-700/60 to-zinc-800/50 shadow-[0_12px_40px_rgba(0,0,0,0.35)] ring-zinc-500/50"
        }`}
      >
        {/* Accent bar */}
        <div
          className={`absolute bottom-0 left-0 right-0 h-1 ${
            isLight ? "bg-kkm-green/90" : "bg-kkm-green"
          }`}
          aria-hidden
        />
        {/* Number: invisible spacer reserves space for "5 000+" so the box doesn't grow */}
        <div className="relative inline-block">
          <span className={`invisible ${numberClasses}`} aria-hidden>
            {finalFormatted}
          </span>
          <span
            className={`absolute inset-0 flex items-center justify-center ${numberClasses}`}
          >
            {formatted}
          </span>
        </div>
        <span
          className={`mt-1.5 text-sm font-semibold tracking-wide sm:text-base ${labelClass}`}
        >
          {label}
        </span>
      </div>
    </div>
  )

  if (embedded) {
    return (
      <div ref={sectionRef} aria-label={label}>
        {content}
      </div>
    )
  }

  return (
    <section
      ref={sectionRef}
      className={`border-t border-zinc-700/80 ${REPORT_SECTION_PADDING}`}
      aria-label={label}
    >
      {content}
    </section>
  )
}

const REPORT_FIRST_SECTION_ID = "phenomena" // used for "scroll to content" and anchor
const REPORT_BOY_SECTION_ID = "report-extortion-growth"
const REPORT_SEXUAL_CRIMES_SECTION_ID = "report-sexual-crimes"
const REPORT_PSYCHOLOGICAL_IMPACT_BAR_SECTION_ID =
  "report-psychological-impact-bars"
const REPORT_SCHOOL_SUPPORT_SECTION_ID = "school-support"
const REPORT_SOLUTIONS_SECTION_ID = "solutions"
const REPORT_JOURNEY_SECTION_ID = "report-user-journey"
const REPORT_PREVENTION_SECTION_ID = "prevention"

type ReportNavGroup = {
  id: string
  label: string
  dotClassName: string
}

const AnnualReport2025Page = () => {
  const { locale } = useContext(LocaleContext)
  const translations = useTranslations()
  const report2025Translations = (translations?.report2025 as Record<string, any>) || {}

  const reportContent = useMemo(() => {
    if (!report2025Translations || typeof report2025Translations !== "object") {
      return {}
    }

    const mergedReport = { ...report2025Translations } as Record<string, any>
    const translatedAiCaseTiles = Array.isArray(report2025Translations.aiCaseTiles)
      ? report2025Translations.aiCaseTiles
      : []

    // Keep structural metadata (ids, ordering, source URLs) in code;
    // translations can only override display text fields.
    mergedReport.aiCaseTiles = ANNUAL_REPORT_2025_STATIC.aiCaseTileIds.map((id, index) => ({
      id,
      ...(translatedAiCaseTiles[index] || {}),
    }))
    mergedReport.teacherSectionText2Source = {
      ...(report2025Translations.teacherSectionText2Source || {}),
      url: ANNUAL_REPORT_2025_STATIC.teacherSectionText2SourceUrl,
    }
    mergedReport.journeyVisualStartSource = {
      ...(report2025Translations.journeyVisualStartSource || {}),
      url:
        report2025Translations.journeyVisualStartSource?.url ||
        ANNUAL_REPORT_2025_STATIC.journeyVisualStartSourceUrl,
    }

    return mergedReport
  }, [locale, report2025Translations])

  const {
    title,
    subtitle,
    forewordHeading,
    forewordParagraphs,
    forewordAuthorName,
    forewordAuthorTitle,
    ageDistributionByYear: _ageDistributionByYear,
    ageDistributionHeading,
    ageDistributionText,
    ageDistributionText2,
    ageDistributionText2Highlight,
    crimeTagsBoxTitle,
    crimeTagsBoxIndexNote,
    crimeTagsBoxData: _crimeTagsBoxData,
    crimeTagColors: _crimeTagColors,
    crimeTagEmoji: _crimeTagEmoji,
    boySectionHeading,
    boySectionText1,
    boySectionText2,
    boySectionText2Highlight,
    boySectionOverlayText,
    aiSectionHeading,
    aiSectionText1,
    aiSectionText2,
    aiCaseTiles,
    sexualCrimesHeading,
    sexualCrimesText,
    sexualCrimesText2,
    sexualCrimesText2Highlight,
    crimeTagsBoxClickHint,
    crimeTagTooltip,
    crimeTagLabelDefamation,
    crimeTagLabelSexualCrimes,
    crimeTagLabelIllegalThreat,
    crimeTagTooltipDefamation,
    crimeTagTooltipSexualCrimes,
    crimeTagTooltipIllegalThreat,
    psychologicalSymptomsBarTitle,
    psychologicalSymptomsBarLabelAnxious,
    psychologicalSymptomsBarLabelLowMood,
    psychologicalSymptomsBarLabelAngry,
    psychologicalSymptomsBarLabelHumiliated,
    psychologicalSymptomsBarLabelWorthless,
    psychologicalSymptomsBarLabelDailyDifficulties,
    psychologicalSymptomsBarLabelSelfHarmThoughts,
    psychologicalSymptomsBarLabelSleepDifficulties,
    psychologicalImpactHeading,
    psychologicalImpactFootnote,
    seriousCasesHeading,
    seriousCasesText,
    seriousCasesTextHighlight,
    seriousCasesPieTitle,
    seriousCasesPieLabelCrime,
    seriousCasesPieLabelThreatOfCrime,
    seriousCasesPieLabelNoCrime,
    phoneBansHeading,
    phoneBansText,
    phoneBansText2,
    phoneBansText3,
    perpetratorKnownHeading,
    perpetratorKnownText,
    perpetratorKnownTextHighlight,
    perpetratorKnownPieTitle,
    perpetratorKnownPieLabelFromSchool,
    perpetratorKnownPieLabelFromElsewhere,
    perpetratorKnownPieLabelFromFriendCircles,
    teacherSectionHeading,
    teacherSectionText,
    teacherSectionText2,
    teacherSectionText3,
    teacherSectionText2Source,
    teacherCasesPieData: _teacherCasesPieData,
    teacherCasesPieTitle,
    teacherCasesPieLabelOwnSituation,
    teacherCasesPieLabelStudentSituation,
    totalCasesCount: _totalCasesCount,
    casesCountLabel,
    supportHeading,
    supportText,
    supportText2,
    trustedFlaggerHeading,
    trustedFlaggerText,
    trustedFlaggerTextHighlight,
    trustedFlaggerText2,
    preventionHeading,
    preventionText,
    preventionText2,
    preventionText2Highlight,
    toldAdultPieTitle,
    toldAdultPieData: _toldAdultPieData,
    toldAdultPieLabelYes,
    toldAdultPieLabelNo,
    toldAdultPieLabelUnknown,
    specializedExpertiseHeading,
    specializedExpertiseText1,
    specializedExpertiseText2,
    feedbackHeading,
    feedbackText,
    feedbackText2,
    feedbackPlaceholderQuotes,
    journeyHeading,
    journeyIntro,
    journeyClickHint,
    journeyVisualStart,
    journeyVisualStartDetail,
    journeyVisualStartExample,
    journeyVisualStartSource,
    journeyVisualLeftLabel,
    journeyVisualLeftSteps,
    journeyVisualRightLabel,
    journeyVisualRightSteps,
    expansionHeading,
    expansionText,
    dataIndexHeading,
    dataIndexText,
    dataIndexText2,
    navPhenomena,
    navSchoolSupport,
    navSolutions,
    navPrevention,
    ageChartTitle,
    ageChartShowYearAriaPrefix,
    ageChartTooltipLabel,
    ageChartTooltipValueLabel,
    ageChartXAxisLabel,
    ageChartYAxisLabel,
    platformRisksHeading,
    platformRisksText,
    platformInfoAriaSuffix,
    closeLabel,
    infoLabel,
    socialPlatformSectionTitle,
    socialPlatformClickHint,
    socialPlatformNote,
    socialPlatformDrawerTexts,
    aiCaseClickHint,
    aiCaseOpenAriaSuffix,
    aiCaseDialogAriaLabel,
    sourceFallbackStt,
    teacherSectionText2Highlight,
    supportTextHighlight,
    feedbackTextHighlight,
    specializedExpertiseText1Highlight,
    preventionText2HighlightCustom,
    dataIndexText2HighlightCustom,
    expansionClickHint,
    psychologicalImpactText,
    psychologicalImpactTextHighlight,
    swedenHotspots,
    swedenHotspotsImageAlt,
    contactHeading,
    contactDescription,
    contactDescriptionSubheading,
    contactDescriptionSecond,
  } = reportContent

  // Keep numeric/statistical datasets in code, not in translation files.
  const {
    ageDistributionByYear,
    crimeTagsBoxData,
    crimeTagColors,
    crimeTagEmoji,
    psychologicalSymptomsBarData: staticPsychologicalSymptomsBarData,
    seriousCasesPieData: staticSeriousCasesPieData,
    perpetratorKnownPieData: staticPerpetratorKnownPieData,
    teacherCasesPieData: staticTeacherCasesPieData,
    totalCasesCount,
    toldAdultPieData: staticToldAdultPieData,
  } = ANNUAL_REPORT_2025_STATIC

  const translatedPsychologicalSymptomsLabels = [
    psychologicalSymptomsBarLabelAnxious,
    psychologicalSymptomsBarLabelLowMood,
    psychologicalSymptomsBarLabelAngry,
    psychologicalSymptomsBarLabelHumiliated,
    psychologicalSymptomsBarLabelWorthless,
    psychologicalSymptomsBarLabelDailyDifficulties,
    psychologicalSymptomsBarLabelSelfHarmThoughts,
    psychologicalSymptomsBarLabelSleepDifficulties,
  ]

  const psychologicalSymptomsBarData = staticPsychologicalSymptomsBarData.map(
    (item, index) => ({
      ...item,
      name: translatedPsychologicalSymptomsLabels[index] || item.name,
    }),
  )

  const translatedSeriousCasesPieLabels = [
    seriousCasesPieLabelCrime,
    seriousCasesPieLabelThreatOfCrime,
    seriousCasesPieLabelNoCrime,
  ]

  const seriousCasesPieData = staticSeriousCasesPieData.map((item, index) => ({
    ...item,
    name: translatedSeriousCasesPieLabels[index] || item.name,
  }))

  const translatedPerpetratorKnownPieLabels = [
    perpetratorKnownPieLabelFromSchool,
    perpetratorKnownPieLabelFromElsewhere,
    perpetratorKnownPieLabelFromFriendCircles,
  ]

  const perpetratorKnownPieData = staticPerpetratorKnownPieData.map(
    (item, index) => ({
      ...item,
      name: translatedPerpetratorKnownPieLabels[index] || item.name,
    }),
  )

  const translatedTeacherCasesPieLabels = [
    teacherCasesPieLabelOwnSituation,
    teacherCasesPieLabelStudentSituation,
  ]

  const teacherCasesPieData = staticTeacherCasesPieData.map((item, index) => ({
    ...item,
    name: translatedTeacherCasesPieLabels[index] || item.name,
  }))

  const translatedToldAdultPieLabels = [
    toldAdultPieLabelYes,
    toldAdultPieLabelNo,
    toldAdultPieLabelUnknown,
  ]

  const toldAdultPieData = staticToldAdultPieData.map((item, index) => ({
    ...item,
    name: translatedToldAdultPieLabels[index] || item.name,
  }))

  const reportNavGroups: ReportNavGroup[] = [
    {
      id: REPORT_FIRST_SECTION_ID,
      label: navPhenomena,
      dotClassName: "bg-zinc-500",
    },
    {
      id: REPORT_SCHOOL_SUPPORT_SECTION_ID,
      label: navSchoolSupport,
      dotClassName: "bg-kkm-yellow",
    },
    {
      id: REPORT_SOLUTIONS_SECTION_ID,
      label: navSolutions,
      dotClassName: "bg-kkm-green",
    },
    {
      id: REPORT_PREVENTION_SECTION_ID,
      label: navPrevention,
      dotClassName: "bg-white",
    },
  ]
  const platformInfoSuffix = platformInfoAriaSuffix
  const closeButtonLabel = closeLabel
  const infoDialogLabel = infoLabel
  const aiDialogLabel = aiCaseDialogAriaLabel
  const translatedSwedenHotspots = Array.isArray(swedenHotspots) ? swedenHotspots : []
  const effectiveSwedenHotspots = SWEDEN_HOTSPOTS.map((hotspot, index) => ({
    ...hotspot,
    label: translatedSwedenHotspots[index]?.label || "",
    text: translatedSwedenHotspots[index]?.text || "",
  }))

  const ageChartYears = Object.keys(ageDistributionByYear).sort()
  const [ageChartYear, setAgeChartYear] = useState(
    ageChartYears[ageChartYears.length - 1] ?? "2025",
  )
  const ageDistributionData = ageDistributionByYear[ageChartYear] ?? []

  const [hoveredJourneyStep, setHoveredJourneyStep] = useState<{
    stepId: string
    label: string
    detail: React.ReactNode
    detailExample?: string
  } | null>(null)
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformInfo | null>(
    null,
  )
  const selectedPlatformDescriptionParagraphs = selectedPlatform
    ? getRiskDescriptionParagraphs(selectedPlatform)
    : []
  const [activeSwedenHotspotId, setActiveSwedenHotspotId] = useState<
    string | null
  >(null)
  const activeExpansionHotspot = activeSwedenHotspotId
    ? effectiveSwedenHotspots.find((h) => h.id === activeSwedenHotspotId) ?? null
    : null
  const latestCrimeTagData = (crimeTagsBoxData[crimeTagsBoxData.length - 1] ||
    {}) as Record<string, number | string>
  const translatedCrimeTagConfig = [
    {
      key: CRIME_TAG_KEY_SEXUAL_CRIMES,
      label: crimeTagLabelSexualCrimes || CRIME_TAG_KEY_SEXUAL_CRIMES,
      tooltip:
        crimeTagTooltipSexualCrimes ||
        (crimeTagTooltip && typeof crimeTagTooltip === "object"
          ? crimeTagTooltip[CRIME_TAG_KEY_SEXUAL_CRIMES]
          : undefined),
    },
    {
      key: CRIME_TAG_KEY_DEFAMATION,
      label: crimeTagLabelDefamation || CRIME_TAG_KEY_DEFAMATION,
      tooltip:
        crimeTagTooltipDefamation ||
        (crimeTagTooltip && typeof crimeTagTooltip === "object"
          ? crimeTagTooltip[CRIME_TAG_KEY_DEFAMATION]
          : undefined),
    },
    {
      key: CRIME_TAG_KEY_ILLEGAL_THREAT,
      label: crimeTagLabelIllegalThreat || CRIME_TAG_KEY_ILLEGAL_THREAT,
      tooltip:
        crimeTagTooltipIllegalThreat ||
        (crimeTagTooltip && typeof crimeTagTooltip === "object"
          ? crimeTagTooltip[CRIME_TAG_KEY_ILLEGAL_THREAT]
          : undefined),
    },
  ]
  const localizedCrimeTagsBoxData = [
    translatedCrimeTagConfig.reduce(
      (acc, item) => {
        acc[item.label] = Number(latestCrimeTagData[item.key] ?? 0)
        return acc
      },
      { year: String(latestCrimeTagData.year ?? "2025") } as Record<string, any>,
    ),
  ]
  const localizedCrimeTagEmoji = translatedCrimeTagConfig.reduce(
    (acc, item) => {
      acc[item.label] = crimeTagEmoji[item.key]
      return acc
    },
    {} as Record<string, string>,
  )
  const localizedCrimeTagTooltip = translatedCrimeTagConfig.reduce(
    (acc, item) => {
      if (typeof item.tooltip === "string" && item.tooltip.trim().length > 0) {
        acc[item.label] = item.tooltip
      }
      return acc
    },
    {} as Record<string, string>,
  )
  const socialPlatformTextOverrides = (
    [
      "snapchat",
      "tiktok",
      "whatsapp",
      "other",
      "instagram",
      "games",
      "discord",
    ] as PlatformId[]
  ).reduce<
    Partial<
      Record<
        PlatformId,
        Pick<
          PlatformInfo,
          "name" | "stats" | "risksDescription" | "extraStats"
        >
      >
    >
  >((acc, id) => {
    const content =
      socialPlatformDrawerTexts && typeof socialPlatformDrawerTexts === "object"
        ? socialPlatformDrawerTexts[id]
        : undefined
    if (!content || typeof content !== "object") return acc

    const name = typeof content.name === "string" ? content.name : undefined
    const stats = typeof content.stats === "string" ? content.stats : undefined
    const risksDescription =
      typeof content.risksDescription === "string"
        ? content.risksDescription
        : undefined
    const extraStats = Array.isArray(content.extraStats)
      ? content.extraStats
          .filter((line: unknown): line is string => typeof line === "string")
          .filter((line): line is string => Boolean(line))
      : undefined

    if (!name && !stats && !risksDescription && !extraStats?.length) return acc

    const fallbackPlatform = defaultPlatforms.find((platform) => platform.id === id)
    if (!fallbackPlatform) return acc

    acc[id] = {
      name: name || fallbackPlatform.name,
      stats: stats || fallbackPlatform.stats,
      risksDescription: risksDescription || fallbackPlatform.risksDescription,
      extraStats:
        extraStats && extraStats.length > 0
          ? extraStats
          : fallbackPlatform.extraStats,
    }
    return acc
  }, {})
  const feedbackQuotesRef = useRef<HTMLDivElement | null>(null)
  const feedbackQuotesInView = useInView(feedbackQuotesRef, {
    amount: 0.2,
    once: true,
  })
  const sextortionOverlayRef = useRef<HTMLDivElement | null>(null)
  const sextortionOverlayInView = useInView(sextortionOverlayRef, {
    amount: 0.25,
    once: true,
  })

  return (
    <div className="min-h-screen bg-white">
      <SEO title={title || ""} description={subtitle || ""} />
      <style>{`#annual-report-content { --report-content-width: ${REPORT_CONTENT_WIDTH_REM}rem; --report-flush-offset: ${REPORT_FLUSH_OFFSET_REM}rem; }`}</style>
      <div id="annual-report-content" className="min-h-screen">
        <Hero
          title={title}
          description=""
          scrollTargetId={REPORT_FIRST_SECTION_ID}
          descriptionFadeIn={false}
          scrollArrowDelay={600}
          imageAnimate={false}
          showScrollArrow
        >
          {/* ── CEO foreword ── */}
          <div className="mx-auto mt-0 w-full max-w-3xl text-left sm:mt-1 2xl:max-w-2xl">
            <div className="rounded-2xl border border-white/10 bg-black/22 px-5 py-5 sm:px-6 sm:py-6 2xl:px-8 2xl:py-10">
              <div className="mb-4 flex gap-3 2xl:mb-5">
                <span
                  className="text-4xl leading-none text-white/50 2xl:text-5xl"
                  aria-hidden="true"
                >
                  ❝
                </span>
                <span className="mt-2 text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-zinc-400 sm:text-xs 2xl:text-sm">
                  {forewordHeading}
                </span>
              </div>
              <div className="space-y-4 2xl:space-y-5">
                {forewordParagraphs.map((paragraph, i) => (
                  <p
                    key={i}
                    className="text-sm leading-relaxed text-white/80 sm:text-base sm:leading-relaxed 2xl:text-lg"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="mt-5 flex items-center justify-start gap-3 border-t border-white/10 pt-4 md:flex-row-reverse 2xl:mt-7 2xl:pt-5">
                <StaticImage
                  src="../../../images/team/suvi-square.jpg"
                  alt={forewordAuthorName}
                  width={60}
                  height={60}
                  className="shrink-0 rounded-full ring-2 ring-white/20"
                  imgClassName="rounded-full"
                />
                <div className="md:text-right">
                  <p className="m-0 text-sm font-bold text-white 2xl:text-base">
                    {forewordAuthorName}
                  </p>
                  <p className="m-0 text-xs text-zinc-400 2xl:text-sm">
                    {forewordAuthorTitle}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Hero>
        {/* ── Sticky table of contents (reuses NavBar from materials) ── */}
        <NavBar
          sections={reportNavGroups.map((g) => ({
            id: g.id,
            title: g.label,
            dotClassName: g.dotClassName,
          }))}
          theme="dark"
          compact
        />

        {/* Report body: each section full-bleed bg; content in max-w-[100rem]. */}
        <ReportSection
          id={REPORT_FIRST_SECTION_ID}
          bg={REPORT_BLACK_BG}
          rowClassName="flex w-full flex-col gap-8 lg:gap-10"
          sectionClassName="report-anchor"
        >
          <div className="flex w-full flex-col items-center gap-10 lg:flex-row lg:items-center lg:gap-10">
            <div className="w-full shrink-0 pl-6 lg:w-[380px] lg:pl-8 xl:w-[440px]">
              <p className="mb-2 text-left text-base font-semibold text-zinc-300">
                {ageChartTitle}
              </p>
              <div className="mb-3 flex flex-wrap justify-start gap-1">
                {ageChartYears.map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => setAgeChartYear(year)}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                      ageChartYear === year
                        ? "bg-kkm-green text-zinc-900"
                        : "bg-zinc-700/80 text-zinc-300 hover:bg-zinc-600"
                    }`}
                    aria-pressed={ageChartYear === year}
                    aria-label={`${ageChartShowYearAriaPrefix} ${year}`}
                  >
                    {year}
                  </button>
                ))}
              </div>
              <div className="h-80 w-full lg:h-72">
                <YearBarChart
                  chartData={ageDistributionData}
                  dark
                  xDataKey="age"
                  valueKey="cases"
                  tooltipLabel={ageChartTooltipLabel}
                  tooltipValueLabel={ageChartTooltipValueLabel}
                  valueSuffix="%"
                  xAxisLabel={ageChartXAxisLabel}
                  yAxisLabel={ageChartYAxisLabel}
                />
              </div>
            </div>
            <ReportContentColumn>
              <h2 className="mb-6 text-2xl font-bold tracking-tight text-zinc-100 sm:text-3xl">
                {ageDistributionHeading}
              </h2>
              <p className="text-lg leading-relaxed text-zinc-400">
                {ageDistributionText}
              </p>
              <p className="mt-4 text-lg leading-relaxed text-zinc-400">
                {textWithHighlight(
                  ageDistributionText2,
                  ageDistributionText2Highlight,
                  "font-semibold text-zinc-200",
                )}
              </p>
            </ReportContentColumn>
          </div>
        </ReportSection>

        {/* Social platforms: text left, bubbles right; click platform → overlay on text */}
        <ReportSection
          id="report-platform-risks"
          bg={REPORT_GRAY_BG}
          sectionClassName="report-anchor"
        >
          <ReportContentColumn>
            <div className="relative min-h-[18rem] overflow-visible">
              <h2 className="text-2xl font-bold tracking-tight text-zinc-100 sm:text-3xl">
                {platformRisksHeading ||
                  "Rikostyypit ja haasteet vaihtelevat sovelluksittain"}
              </h2>
              <p className="text-lg leading-relaxed text-zinc-400">
                {platformRisksText ||
                  "Sovelluskohtaiset käyttökulttuurit, tekniset ominaisuudet ja algoritmit luovat ympäristöjä, joissa uhat ilmenevät eri tavoin. Verkkohäirintään erikoistunut asiantuntijatiimimme seuraa näitä kehityskulkuja jatkuvasti, jotta voimme varmistaa tehokkaan ja nopean avun sovelluksesta riippumatta."}
              </p>
              {selectedPlatform && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 hidden overflow-auto rounded-xl bg-zinc-800 p-5 pr-12 shadow-lg ring-1 ring-zinc-700/50 sm:block lg:bottom-auto lg:right-[-2rem] lg:top-[-2.25rem] lg:h-[22rem]"
                  role="dialog"
                  aria-label={`${selectedPlatform.name} – ${platformInfoSuffix}`}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedPlatform(null)}
                    className="absolute right-3 top-3 rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-700/80 hover:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-kkm-green/50"
                    aria-label={closeButtonLabel}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                  <p className="text-sm font-semibold text-kkm-green">
                    {selectedPlatform.stats}
                  </p>
                  {selectedPlatformDescriptionParagraphs.map(
                    (paragraph, index) => (
                      <p
                        key={`${selectedPlatform.id}-selected-paragraph-${index}`}
                        className={
                          index === 0
                            ? "mt-3 text-sm leading-relaxed text-zinc-300"
                            : "mt-4 text-sm leading-relaxed text-zinc-300"
                        }
                      >
                        {paragraph}
                      </p>
                    ),
                  )}
                  {selectedPlatform.extraStats &&
                    selectedPlatform.extraStats.length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {selectedPlatform.extraStats.map((line, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-xs text-zinc-400"
                          >
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-zinc-500" />
                            {line}
                          </li>
                        ))}
                      </ul>
                    )}
                  {selectedPlatform.id !== "other" && (
                    <p className="mt-3 text-xs italic leading-relaxed text-zinc-500">
                      {socialPlatformNote}
                    </p>
                  )}
                </motion.div>
              )}
              <MobileBottomSheet
                open={!!selectedPlatform}
                onClose={() => setSelectedPlatform(null)}
                ariaLabel={
                  selectedPlatform
                    ? `${selectedPlatform.name} – ${platformInfoSuffix}`
                    : infoDialogLabel
                }
                variant="dark"
              >
                {selectedPlatform && (
                  <>
                    <p className="text-xl font-semibold text-kkm-green">
                      {selectedPlatform.stats}
                    </p>
                    {selectedPlatformDescriptionParagraphs.map(
                      (paragraph, index) => (
                        <p
                          key={`${selectedPlatform.id}-mobile-paragraph-${index}`}
                          className={
                            index === 0
                              ? "mt-3 text-sm leading-relaxed text-zinc-300"
                              : "mt-4 text-sm leading-relaxed text-zinc-300"
                          }
                        >
                          {paragraph}
                        </p>
                      ),
                    )}
                    {selectedPlatform.extraStats &&
                      selectedPlatform.extraStats.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {selectedPlatform.extraStats.map((line, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-xs text-zinc-400"
                            >
                              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-zinc-500" />
                              {line}
                            </li>
                          ))}
                        </ul>
                      )}
                    {selectedPlatform.id !== "other" && (
                      <p className="mt-3 text-xs italic leading-relaxed text-zinc-500">
                        {socialPlatformNote}
                      </p>
                    )}
                  </>
                )}
              </MobileBottomSheet>
            </div>
          </ReportContentColumn>
          <div className="w-full shrink-0 lg:w-[600px] lg:min-w-[380px] lg:pr-12 xl:w-[680px]">
            <SocialPlatformDrawer
              sectionTitle={socialPlatformSectionTitle}
              clickHint={socialPlatformClickHint}
              platformTextOverrides={socialPlatformTextOverrides}
              selectedId={selectedPlatform?.id ?? null}
              onSelectPlatform={setSelectedPlatform}
            />
          </div>
        </ReportSection>

        {/* Serious cases: pie left, text right */}
        <ReportSection
          id="report-serious-cases"
          bg={REPORT_BLACK_BG}
          sectionClassName="report-anchor"
        >
          <ReportMediaColumn>
            <div className="h-64 w-full lg:h-72">
              <TeacherPieChartPlaceholder
                pieData={seriousCasesPieData}
                title={seriousCasesPieTitle}
              />
            </div>
          </ReportMediaColumn>
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-100 sm:text-3xl">
              {seriousCasesHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-400">
              {textWithHighlight(
                seriousCasesText,
                seriousCasesTextHighlight,
                "font-semibold text-zinc-200",
              )}
            </p>
          </ReportContentColumn>
        </ReportSection>

        {/* Sexual crimes / Crime tags: text + tag list (no flush) */}
        <ReportSection
          id={REPORT_SEXUAL_CRIMES_SECTION_ID}
          bg={REPORT_GRAY_BG}
          sectionClassName="report-anchor 2xl:[&>div]:!pt-16"
          rowClassName="flex w-full flex-col items-center gap-6 lg:flex-row lg:items-center lg:gap-6"
        >
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-100 sm:text-3xl">
              {sexualCrimesHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-400">
              {sexualCrimesText}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-zinc-400">
              {textWithHighlight(sexualCrimesText2, sexualCrimesText2Highlight)}
            </p>
          </ReportContentColumn>
          <div className="w-full shrink-0 pr-2 sm:pr-6 lg:-mt-44 lg:w-[500px] lg:pr-10 xl:w-[580px]">
            <div className="h-auto min-h-[26rem] w-full lg:h-72 lg:min-h-0">
              <CrimeTagsBox
                data={localizedCrimeTagsBoxData}
                title={crimeTagsBoxTitle}
                clickHint={crimeTagsBoxClickHint}
                indexNote={crimeTagsBoxIndexNote}
                emojiMap={localizedCrimeTagEmoji}
                tooltipMap={localizedCrimeTagTooltip}
                dark
              />
            </div>
          </div>
        </ReportSection>

        {/* Boy: text + flush-right image (report-flush-right) */}
        <ReportSection
          id={REPORT_BOY_SECTION_ID}
          bg={REPORT_BLACK_BG}
          rowClassName="flex w-full flex-col items-center gap-10 lg:flex-row lg:items-stretch lg:gap-10 2xl:items-center"
          sectionClassName="report-anchor"
        >
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-100 sm:text-3xl">
              {boySectionHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-400">
              {boySectionText1}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-zinc-400">
              {textWithHighlight(boySectionText2, boySectionText2Highlight)}
            </p>
          </ReportContentColumn>
          <div className="report-flush-right relative order-first flex min-h-[18rem] w-full shrink-0 items-center sm:min-h-[22rem] lg:order-none lg:min-h-[36rem] lg:w-[50vw]">
            <div className="h-full w-full overflow-hidden rounded-l-full">
              <StaticImage
                src="../../../images/annual-reports/boy6.jpg"
                alt=""
                layout="fullWidth"
                placeholder="blurred"
                className="!h-full !min-h-[18rem] !w-full sm:!min-h-[21rem] lg:!min-h-[34rem] xl:!min-h-[41rem] 2xl:!h-[39rem] 2xl:!min-h-0"
                imgClassName="object-cover object-center"
              />
            </div>
            <motion.div
              ref={sextortionOverlayRef}
              className="pointer-events-none absolute left-3 top-3 z-10 sm:left-4 sm:top-4 lg:left-6 lg:top-6"
              variants={sextortionOverlayVariants}
              initial="hidden"
              animate={sextortionOverlayInView ? "visible" : "hidden"}
            >
              <motion.div
                className="text-2xl font-black leading-none text-red-600 sm:text-4xl lg:text-5xl"
                variants={sextortionOverlayItemVariants}
              >
                71 %
              </motion.div>
              <motion.div
                className="mt-1.5 max-w-[9rem] text-xs font-black leading-snug text-red-600 sm:mt-2 sm:max-w-[12rem] sm:text-base sm:leading-tight lg:max-w-[13.5rem] lg:text-lg lg:leading-6"
                variants={sextortionOverlayItemVariants}
              >
                <span
                  className="rounded-xl bg-red-200 box-decoration-clone px-2 py-0.5"
                >
                  {boySectionOverlayText || boySectionText2Highlight}
                </span>
              </motion.div>
            </motion.div>
          </div>
        </ReportSection>

        {/* Psychological impact (comparison): horizontal bars left, same text right */}
        <ReportSection
          id={REPORT_PSYCHOLOGICAL_IMPACT_BAR_SECTION_ID}
          bg={REPORT_GRAY_BG}
          sectionClassName="report-anchor"
          rowClassName="flex w-full flex-col items-center gap-10 lg:flex-row lg:items-start lg:gap-6"
        >
          <ReportMediaColumn className="-ml-3 w-[calc(100%+0.75rem)] pr-0 lg:ml-0 lg:w-[580px] lg:pr-6 xl:w-[760px]">
            <div className="h-[30rem] w-full sm:h-[34rem] lg:h-96">
              <HorizontalSymptomsBarChartPlaceholder
                title={psychologicalSymptomsBarTitle}
                chartData={psychologicalSymptomsBarData}
                dark
              />
            </div>
            {psychologicalImpactFootnote ? (
              <p className="mt-12 text-xs leading-relaxed text-zinc-500 lg:mt-9">
                {psychologicalImpactFootnote}
              </p>
            ) : null}
          </ReportMediaColumn>
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-100 sm:text-3xl">
              {psychologicalImpactHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-400">
              {textWithHighlight(
                psychologicalImpactText,
                psychologicalImpactTextHighlight,
              )}
            </p>
          </ReportContentColumn>
        </ReportSection>

        {/* Kids / AI: flush-left clickable tiles + text */}
        <AICaseTilesSection
          id="report-ai-cases"
          tiles={aiCaseTiles}
          heading={aiSectionHeading}
          text1={aiSectionText1}
          text2={aiSectionText2}
          clickHint={aiCaseClickHint}
          openAriaSuffix={aiCaseOpenAriaSuffix}
        />

        {/* Phone bans: text + image (yellow 1 – normal) */}
        <ReportSection
          id={REPORT_SCHOOL_SUPPORT_SECTION_ID}
          bg={REPORT_YELLOW_BG}
          sectionClassName="report-anchor"
          rowClassName="flex w-full flex-col items-center gap-6 lg:flex-row lg:items-center lg:gap-6"
        >
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900 sm:text-3xl">
              {phoneBansHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {phoneBansText}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {phoneBansText2}
            </p>
            {phoneBansText3 ? (
              <p className="mt-4 text-lg leading-relaxed text-zinc-800">
                {phoneBansText3}
              </p>
            ) : null}
          </ReportContentColumn>
          <FlushRightImage>
            <StaticImage
              src="../../../images/annual-reports/ban1.jpg"
              alt=""
              layout="fullWidth"
              placeholder="blurred"
              className="!h-full !min-h-[20rem] !w-full sm:!min-h-[24rem] lg:!min-h-[38rem] xl:!min-h-[38rem] 2xl:!h-[33rem] 2xl:!min-h-0"
              imgClassName="object-cover object-center"
            />
          </FlushRightImage>
        </ReportSection>

        {/* Perpetrator known from: pie (left) + text (yellow 2 – light) */}
        <ReportSection
          id="report-known-perpetrator"
          bg={REPORT_YELLOW_LIGHT_BG}
          sectionClassName="report-anchor"
        >
          <ReportMediaColumn className="order-2 pl-0 lg:order-1 lg:pl-8">
            <div className="h-64 w-full lg:h-72">
              <TeacherPieChartPlaceholder
                pieData={perpetratorKnownPieData}
                title={perpetratorKnownPieTitle}
                dark={false}
                bolderLabels
              />
            </div>
          </ReportMediaColumn>
          <ReportContentColumn className="order-1 lg:order-2">
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900 sm:text-3xl">
              {perpetratorKnownHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {textWithHighlight(
                perpetratorKnownText,
                perpetratorKnownTextHighlight,
                "font-bold text-zinc-800",
              )}
            </p>
          </ReportContentColumn>
        </ReportSection>

        {/* Teacher: Asiantuntijatuki osana kouluturvallisuutta (yellow 3 – normal) */}
        <ReportSection
          id="report-staff-support"
          bg={REPORT_YELLOW_BG}
          sectionClassName="report-anchor [&>div]:!py-10 lg:[&>div]:!py-12"
        >
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-800 sm:text-3xl">
              {teacherSectionHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {textWithSourcePlaceholder(
                teacherSectionText,
                teacherSectionText2Source,
                sourceFallbackStt,
              )}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {textWithHighlight(
                teacherSectionText2,
                teacherSectionText2Highlight ||
                  "31 % tapauksista henkilökunnan edustaja pyysi apua omaan tilanteeseensa",
                "font-bold text-zinc-800",
              )}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {teacherSectionText3}
            </p>
          </ReportContentColumn>
          <ReportMediaColumn side="right">
            <div className="h-64 w-full lg:h-72">
              <TeacherPieChartPlaceholder
                pieData={teacherCasesPieData}
                title={teacherCasesPieTitle}
                dark={false}
                bolderLabels
              />
            </div>
          </ReportMediaColumn>
        </ReportSection>

        {/* Support: full-bleed teal band */}
        <ReportSection
          id={REPORT_SOLUTIONS_SECTION_ID}
          bg={REPORT_TEAL_BG}
          clipOverflow={false}
          sectionClassName="report-anchor"
        >
          <div className="flex w-full shrink-0 items-center justify-center lg:w-[380px] lg:pl-8 xl:w-[440px] xl:pl-12">
            <CountUpSection
              target={totalCasesCount}
              label={casesCountLabel}
              durationMs={2800}
              embedded
              variant="light"
              suffix="+"
            />
          </div>
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-800 sm:text-3xl">
              {supportHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {textWithHighlight(
                supportText,
                supportTextHighlight ||
                  "90 % avunpyytäjistä sai avun vuorokauden kuluessa.",
                "font-bold text-zinc-800",
              )}
            </p>
            {supportText2 ? (
              <p className="mt-4 text-lg leading-relaxed text-zinc-800">
                {supportText2}
              </p>
            ) : null}
          </ReportContentColumn>
        </ReportSection>

        {/* Feedback: Asiakkaidemme palaute */}
        <ReportSection
          id="report-feedback"
          bg={REPORT_TEAL_LIGHT_BG}
          rowClassName="flex w-full flex-col items-center gap-10 lg:flex-row lg:items-stretch lg:gap-10 2xl:items-center"
          sectionClassName="report-anchor"
        >
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-800 sm:text-3xl">
              {feedbackHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {textWithHighlight(
                feedbackText,
                feedbackTextHighlight ||
                  "94 % kertoi, että avulla oli ollut positiivinen vaikutus tilanteeseen.",
                "font-bold text-zinc-800",
              )}
            </p>
            {feedbackText2 ? (
              <p className="mt-4 text-lg leading-relaxed text-zinc-800">
                {feedbackText2}
              </p>
            ) : null}
          </ReportContentColumn>
          <div
            ref={feedbackQuotesRef}
            className="report-flush-right relative order-first flex min-h-[18rem] w-full shrink-0 items-center sm:min-h-[22rem] lg:order-none lg:min-h-[36rem] lg:w-[56vw] 2xl:h-[32rem] 2xl:min-h-0"
          >
            <div className="h-full w-full overflow-hidden rounded-l-full">
              <motion.div
                className="relative h-full w-full overflow-hidden"
                variants={feedbackQuotesGridVariants}
                initial="hidden"
                animate={feedbackQuotesInView ? "visible" : "hidden"}
              >
                <img
                  src={
                    require("../../../images/annual-reports/feedback1.jpg")
                      .default
                  }
                  alt=""
                  className="h-full min-h-[23rem] w-full object-cover object-right sm:min-h-[28rem] lg:min-h-[36rem] 2xl:h-[32rem] 2xl:min-h-0"
                  loading="lazy"
                />
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.28)_0%,rgba(0,0,0,0.16)_35%,rgba(0,0,0,0.22)_100%)]" />
              </motion.div>
            </div>
            <div className="pointer-events-none absolute inset-0 z-10">
              {(feedbackPlaceholderQuotes ?? [])
                .slice(0, feedbackQuoteOverlayPositions.length)
                .map((q, i) => (
                  <div
                    key={i}
                    className={`absolute ${feedbackQuoteOverlayPositions[i]}`}
                  >
                    <QuoteCard
                      text={q.quote}
                      variant={i % 2 === 0 ? "green" : "white"}
                      tailPosition={
                        i % 2 === 0 ? "bottom-right" : "bottom-left"
                      }
                      delay={0.2 + i * 0.15}
                      index={i}
                      inView={feedbackQuotesInView}
                    />
                  </div>
                ))}
            </div>
          </div>
        </ReportSection>

        {/* Specialized expertise / Anonyymi kanava */}
        <ReportSection
          id="report-anonymous-support"
          bg={REPORT_TEAL_BG}
          sectionClassName="report-anchor [&>div]:!py-10 lg:[&>div]:!py-12"
        >
          <div className="w-full shrink-0 pl-6 lg:w-[380px] lg:pl-8 xl:w-[440px]">
            <div className="h-72 w-full lg:h-80">
              <TeacherPieChartPlaceholder
                pieData={toldAdultPieData}
                title={toldAdultPieTitle}
                dark={false}
                bolderLabels
              />
            </div>
          </div>
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-800 sm:text-3xl">
              {specializedExpertiseHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {textWithHighlight(
                specializedExpertiseText1,
                specializedExpertiseText1Highlight ||
                  "Nimettömän avun merkitys korostuu etenkin lasten seksuaalista itsemääräämisoikeutta loukkaavissa rikoksissa",
                "font-bold text-zinc-800",
              )}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {specializedExpertiseText2}
            </p>
          </ReportContentColumn>
        </ReportSection>

        {/* Trusted Flagger */}
        <ReportSection
          id="report-trusted-flagger"
          bg={REPORT_TEAL_LIGHT_BG}
          sectionClassName="report-anchor [&>div]:!py-10 lg:[&>div]:!py-12"
        >
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-800 sm:text-3xl">
              {trustedFlaggerHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {textWithHighlight(
                trustedFlaggerText,
                trustedFlaggerTextHighlight,
                "font-bold text-zinc-800",
              )}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {trustedFlaggerText2}
            </p>
          </ReportContentColumn>
          <FlushRightImage>
            <StaticImage
              src="../../../images/annual-reports/eu2.jpg"
              alt=""
              layout="fullWidth"
              placeholder="blurred"
              className="!h-full !min-h-[19rem] !w-full sm:!min-h-[22rem] lg:!min-h-[36rem] 2xl:!h-[32rem] 2xl:!min-h-0"
              imgClassName="object-cover object-center"
            />
          </FlushRightImage>
        </ReportSection>

        {/* User journey visual */}
        <ReportSection
          id={REPORT_JOURNEY_SECTION_ID}
          bg={REPORT_TEAL_BG}
          clipOverflow={false}
          rowClassName="flex w-full flex-col items-center gap-4 lg:flex-row lg:items-start lg:gap-10"
          sectionClassName="report-anchor"
        >
          <div className="hidden w-full shrink-0 items-center justify-center pl-6 lg:flex lg:w-[560px] lg:pl-8 xl:w-[620px] xl:pl-12">
            <div className="w-full max-w-2xl rounded-2xl bg-white px-6 py-8 shadow-[0_8px_32px_rgba(0,0,0,0.12)] ring-1 ring-black/5">
              <UserJourneyVisual
                variant="lightBg"
                startLabel={journeyVisualStart}
                startDetail={journeyVisualStartDetail}
                startExample={journeyVisualStartExample}
                startSource={journeyVisualStartSource}
                leftLabel={journeyVisualLeftLabel}
                leftSteps={journeyVisualLeftSteps}
                rightLabel={journeyVisualRightLabel}
                rightSteps={journeyVisualRightSteps}
                hoveredStep={hoveredJourneyStep}
                onHoverStep={setHoveredJourneyStep}
              />
            </div>
          </div>
          <ReportContentColumn className="justify-start lg:justify-start">
            <h2 className="text-2xl font-bold tracking-tight text-zinc-800 sm:text-3xl">
              {journeyHeading}
            </h2>
            <div className="relative mt-4 hidden h-[360px] overflow-visible lg:block xl:h-[400px]">
              {hoveredJourneyStep ? (
                (() => {
                  const isSometurvaPath =
                    hoveredJourneyStep.stepId.startsWith("right-")
                  const isLonelinessNode = hoveredJourneyStep.stepId === "left-0"
                  const isCrisisNode = hoveredJourneyStep.stepId === "start"
                  const isEscalationNode = hoveredJourneyStep.stepId === "left-1"
                  const isConsequencesNode =
                    hoveredJourneyStep.stepId === "left-2"
                  const isAnonymousHelpNode =
                    hoveredJourneyStep.stepId === "right-0"
                  const isActionsNode = hoveredJourneyStep.stepId === "right-1"
                  const isResolutionNode = hoveredJourneyStep.stepId === "right-2"
                  const isRoomierTextNode =
                    isAnonymousHelpNode || isActionsNode || isResolutionNode
                  const isExtraRoomyTextNode = isActionsNode
                  const isImagePrioritySometurvaNode =
                    isAnonymousHelpNode || isActionsNode || isResolutionNode
                  return (
                    <div
                      className={`relative z-20 h-full overflow-hidden rounded-xl px-6 py-5 pr-10 shadow-[0_16px_40px_rgba(0,0,0,0.22)] sm:pr-12 lg:-mt-14 lg:h-[calc(100%+4rem)] xl:-mt-[4.5rem] xl:h-[calc(100%+5rem)] ${
                        isSometurvaPath
                          ? "border border-kkm-green/50 bg-white ring-1 ring-kkm-green/40"
                          : "bg-zinc-800"
                      }`}
                    >
                      <div
                        className={`pointer-events-none absolute inset-y-0 right-0 hidden sm:block ${
                          isExtraRoomyTextNode
                            ? "w-[38%]"
                            : isRoomierTextNode
                              ? "w-[52%]"
                              : "w-[56%]"
                        }`}
                      >
                        {isLonelinessNode ? (
                          <StaticImage
                            src="../../../images/annual-reports/crisis.jpg"
                            alt=""
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        ) : isCrisisNode ? (
                          <StaticImage
                            src="../../../images/annual-reports/lonely.jpg"
                            alt=""
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        ) : isEscalationNode ? (
                          <StaticImage
                            src="../../../images/annual-reports/escalation.jpg"
                            alt=""
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        ) : isConsequencesNode ? (
                          <StaticImage
                            src="../../../images/annual-reports/consequences.jpg"
                            alt=""
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        ) : isAnonymousHelpNode ? (
                          <StaticImage
                            src="../../../images/annual-reports/anonymity.jpg"
                            alt=""
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        ) : isActionsNode ? (
                          <StaticImage
                            src="../../../images/annual-reports/help.jpg"
                            alt=""
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        ) : isResolutionNode ? (
                          <StaticImage
                            src="../../../images/annual-reports/happy.jpg"
                            alt=""
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        ) : (
                          <StaticImage
                            src="../../../images/annual-reports/crisis.jpg"
                            alt=""
                            placeholder="blurred"
                            className="!h-full !w-full"
                            imgClassName="object-cover object-center"
                          />
                        )}
                        <div
                          className={`absolute inset-0 ${
                            isSometurvaPath
                              ? isImagePrioritySometurvaNode
                                ? "bg-[linear-gradient(to_right,rgba(255,255,255,1)_0%,rgba(255,255,255,0.96)_10%,rgba(255,255,255,0.84)_24%,rgba(255,255,255,0.66)_40%,rgba(255,255,255,0.46)_58%,rgba(255,255,255,0.28)_74%,rgba(255,255,255,0.14)_88%,rgba(255,255,255,0.06)_100%)]"
                                : "bg-gradient-to-r from-white via-white/90 to-white/5"
                              : "via-zinc-800/92 bg-gradient-to-r from-zinc-800 to-zinc-800/10"
                          }`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setHoveredJourneyStep(null)}
                        className="absolute right-2 top-2 z-20 rounded-md p-1 text-zinc-500 transition-colors hover:bg-zinc-200/80 hover:text-zinc-800 focus:outline-none focus:ring-2 focus:ring-kkm-green/50 sm:right-3 sm:top-3"
                        aria-label={closeButtonLabel}
                      >
                        <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                      </button>
                      <div
                        className={`relative z-10 min-w-0 flex-1 ${
                          isExtraRoomyTextNode
                            ? "sm:max-w-[80%]"
                            : isRoomierTextNode
                              ? "sm:max-w-[66%]"
                              : isImagePrioritySometurvaNode
                                ? "sm:max-w-[54%]"
                                : "sm:max-w-[66%]"
                        } h-full overflow-y-auto pr-2`}
                      >
                        <p
                          className={`text-lg font-semibold ${isSometurvaPath ? "text-zinc-800" : "text-zinc-100"}`}
                        >
                          {hoveredJourneyStep.label}
                        </p>
                        <p
                          className={`mt-2 text-base leading-relaxed ${isSometurvaPath ? "text-zinc-700" : "text-zinc-300"}`}
                        >
                          {hoveredJourneyStep.detail}
                        </p>
                        {hoveredJourneyStep.detailExample ? (
                          <p
                            className={`mt-3 text-sm italic leading-relaxed ${isSometurvaPath ? "text-zinc-600" : "text-zinc-400"}`}
                          >
                            {hoveredJourneyStep.detailExample}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  )
                })()
              ) : (
                <>
                  {journeyIntro && (
                    <p className="text-lg leading-relaxed text-zinc-800">
                      {journeyIntro}
                    </p>
                  )}
                  {journeyClickHint && (
                    <p className="mt-3 flex items-center gap-2 text-base text-zinc-700">
                      <HandRaisedIcon
                        className="h-4 w-4 shrink-0 text-zinc-800"
                        aria-hidden
                      />
                      <span>{journeyClickHint}</span>
                    </p>
                  )}
                </>
              )}
            </div>
            <>
              {journeyIntro && (
                <p className="mt-4 text-lg leading-relaxed text-zinc-800 lg:hidden">
                  {journeyIntro}
                </p>
              )}
              {journeyClickHint && (
                <p className="mt-3 flex items-center gap-2 text-base text-zinc-700 lg:hidden">
                  <HandRaisedIcon
                    className="h-4 w-4 shrink-0 text-zinc-800"
                    aria-hidden
                  />
                  <span>{journeyClickHint}</span>
                </p>
              )}
            </>
          </ReportContentColumn>
          <div className="mt-0 w-full px-2 lg:hidden">
            <div className="relative w-full rounded-2xl bg-white px-2 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.12)] ring-1 ring-black/5 sm:px-6 sm:py-8">
              {(() => {
                const isSometurvaStep = hoveredJourneyStep
                  ? hoveredJourneyStep.stepId.startsWith("right-")
                  : false
                return (
                  <MobileBottomSheet
                    open={!!hoveredJourneyStep}
                    onClose={() => setHoveredJourneyStep(null)}
                    ariaLabel="Matkan vaihe"
                    hideAbove="lg"
                    variant={isSometurvaStep ? "light" : "dark"}
                  >
                    {hoveredJourneyStep && (
                      <>
                        <p
                          className={`font-semibold ${isSometurvaStep ? "text-zinc-800" : "text-zinc-100"}`}
                        >
                          {hoveredJourneyStep.label}
                        </p>
                        <p
                          className={`mt-2 text-base leading-relaxed ${isSometurvaStep ? "text-zinc-700" : "text-zinc-300"}`}
                        >
                          {hoveredJourneyStep.detail}
                        </p>
                        {hoveredJourneyStep.detailExample ? (
                          <p
                            className={`mt-3 text-sm italic leading-relaxed ${isSometurvaStep ? "text-zinc-600" : "text-zinc-400"}`}
                          >
                            {hoveredJourneyStep.detailExample}
                          </p>
                        ) : null}
                      </>
                    )}
                  </MobileBottomSheet>
                )
              })()}
              <UserJourneyVisual
                variant="lightBg"
                startLabel={journeyVisualStart}
                startDetail={journeyVisualStartDetail}
                startExample={journeyVisualStartExample}
                startSource={journeyVisualStartSource}
                leftLabel={journeyVisualLeftLabel}
                leftSteps={journeyVisualLeftSteps}
                rightLabel={journeyVisualRightLabel}
                rightSteps={journeyVisualRightSteps}
                hoveredStep={hoveredJourneyStep}
                onHoverStep={setHoveredJourneyStep}
                showNodeLabels
                animateOnScroll={false}
              />
            </div>
          </div>
        </ReportSection>

        {/* Prevention: Uusia työkaluja (white 1 – normal) */}
        <ReportSection
          id={REPORT_PREVENTION_SECTION_ID}
          bg={REPORT_WHITE_BG}
          sectionClassName="report-anchor [&>div]:!py-8 lg:[&>div]:!py-10"
          rowClassName={`${REPORT_ROW_CLASS} flex-col-reverse lg:flex-row lg:gap-0`}
        >
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-800 sm:text-3xl">
              {preventionHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {textWithHighlight(
                preventionText,
                preventionText2HighlightCustom,
                "font-bold text-zinc-800",
              )}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {textWithHighlight(
                preventionText2,
                preventionText2Highlight,
                "font-bold text-zinc-800",
              )}
            </p>
          </ReportContentColumn>
          <ReportMediaColumn
            side="right"
            className="-mr-4 pr-0 sm:-mr-6 sm:pr-0 lg:-mr-10 lg:w-[560px] lg:pr-0 xl:-mr-14 xl:w-[740px]"
          >
            <div className="w-full">
              {locale.startsWith("sv") ? (
                <StaticImage
                  src="../../../images/annual-reports/app-se.jpg"
                  alt=""
                  layout="fullWidth"
                  placeholder="blurred"
                  className="!h-auto !w-full"
                  imgStyle={{
                    objectFit: "contain",
                    objectPosition: "center center",
                  }}
                />
              ) : null}
              {locale.startsWith("en") ? (
                <StaticImage
                  src="../../../images/annual-reports/app-en.jpg"
                  alt=""
                  layout="fullWidth"
                  placeholder="blurred"
                  className="!h-auto !w-full"
                  imgStyle={{
                    objectFit: "contain",
                    objectPosition: "center center",
                  }}
                />
              ) : null}
              {!locale.startsWith("sv") && !locale.startsWith("en") ? (
                <StaticImage
                  src="../../../images/annual-reports/app-fi.jpg"
                  alt=""
                  layout="fullWidth"
                  placeholder="blurred"
                  className="!h-auto !w-full"
                  imgStyle={{
                    objectFit: "contain",
                    objectPosition: "center center",
                  }}
                />
              ) : null}
            </div>
          </ReportMediaColumn>
        </ReportSection>

        {/* Data & Someturva-indeksi (white 2 – light) */}
        <ReportSection
          id="report-data-index"
          bg={REPORT_WHITE_LIGHT_BG}
          sectionClassName="report-anchor"
          rowClassName="flex w-full flex-col items-center gap-4 lg:flex-row lg:items-start lg:gap-10 lg:-mb-6 2xl:-mb-10"
        >
          <FlushLeftImage className="max-sm:-ml-4 max-sm:w-[calc(100%+1rem)] lg:w-[50vw] lg:-mt-12 lg:-mb-4 2xl:-mt-16">
            <StaticImage
              src="../../../images/annual-reports/data1.jpg"
              alt=""
              layout="fullWidth"
              placeholder="blurred"
              className="!h-full !w-full lg:!min-h-[28rem] xl:!min-h-[30rem] 2xl:!h-[28rem] 2xl:!min-h-0"
              imgClassName="object-cover object-center"
            />
          </FlushLeftImage>
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-800 sm:text-3xl">
              {dataIndexHeading}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {dataIndexText}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-zinc-800">
              {textWithHighlight(
                dataIndexText2,
                dataIndexText2HighlightCustom,
                "font-bold text-zinc-800",
              )}
            </p>
          </ReportContentColumn>
        </ReportSection>

        {/* Expansion: Someturva laajentuu (Sweden) (white 3 – normal) */}
        <ReportSection
          id="report-expansion"
          bg={REPORT_WHITE_BG}
          clipOverflow={false}
          rowClassName="flex w-full flex-col-reverse items-center gap-4 lg:flex-row lg:items-center lg:gap-10"
          sectionClassName="report-anchor [&>div]:!py-8 lg:[&>div]:!py-10"
        >
          <ReportContentColumn>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-800 sm:text-3xl">
              {expansionHeading}
            </h2>
            <div className="relative mt-4 min-h-[21rem]">
              <p className="text-lg leading-relaxed text-zinc-800">
                {expansionText}
              </p>

              <p className="mt-4 flex items-center gap-2 text-base text-zinc-600">
                <HandRaisedIcon
                  className="h-4 w-4 shrink-0 text-kkm-green"
                  aria-hidden
                />
                <span>
                  {expansionClickHint}
                </span>
              </p>

              {/* Desktop: inline overlay */}
              <AnimatePresence>
                {activeExpansionHotspot ? (
                  <motion.div
                    key="expansion-desktop"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute inset-0 hidden overflow-y-auto rounded-xl border border-zinc-200 bg-white p-4 pr-11 shadow-lg ring-1 ring-black/5 sm:block"
                    role="dialog"
                    aria-label={infoDialogLabel}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveSwedenHotspotId(null)}
                      className="absolute right-3 top-3 rounded-md p-1 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 focus:outline-none focus:ring-2 focus:ring-kkm-green/40"
                      aria-label={closeButtonLabel}
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                    <h4 className="text-base font-semibold text-kkm-green">
                      {activeExpansionHotspot.label}
                    </h4>
                    <p className="mt-2 text-base leading-relaxed text-zinc-700">
                      {activeExpansionHotspot.text}
                    </p>
                  </motion.div>
                ) : null}
              </AnimatePresence>

              <MobileBottomSheet
                open={!!activeExpansionHotspot}
                onClose={() => setActiveSwedenHotspotId(null)}
                ariaLabel={infoDialogLabel}
                variant="light"
              >
                <h4 className="text-xl font-semibold text-kkm-green">
                  {activeExpansionHotspot?.label}
                </h4>
                <p className="mt-3 text-base leading-relaxed text-zinc-700">
                  {activeExpansionHotspot?.text}
                </p>
              </MobileBottomSheet>
            </div>
          </ReportContentColumn>
          <div className="w-full shrink-0 lg:ml-auto lg:w-[600px] lg:translate-x-8 xl:w-[600px] xl:translate-x-12">
            <NordicExpansionViz
              activeId={activeSwedenHotspotId}
              onSelect={(id) => setActiveSwedenHotspotId(id)}
              hotspots={effectiveSwedenHotspots}
              imageAlt={swedenHotspotsImageAlt}
            />
          </div>
        </ReportSection>

        {/* Contact full-bleed: no max-w wrapper so background (zinc-800) reaches screen edges on this page only */}
        <Contact
          theme="dark"
          heading={contactHeading}
          description={contactDescription}
          descriptionSubheading={contactDescriptionSubheading}
          descriptionSecond={contactDescriptionSecond}
          hideOfficeInfo
          imageContainerClassName="lg:w-80 xl:w-[22rem]"
          image={
            <StaticImage
              src="../../../images/annual-reports/family.jpg"
              alt=""
              layout="fullWidth"
              placeholder="blurred"
              className="!h-full !min-h-[20rem] !w-full lg:!min-h-[32rem]"
              imgClassName="object-cover"
            />
          }
        />
      </div>
    </div>
  )
}

export default AnnualReport2025Page
